import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { pdf } from '@react-pdf/renderer';
import Statement from './Statement/Statement';
import Logo from '../../Assets/images/logo.svg';
import wallet from '../../Assets/images/wallet.png';
import contactSupport from '../../images/contact-support.png';
import financialWisdom from '../../images/financial-wisdom.png';
import privacyFirst from '../../images/privacy-first.png';
import overviewImage from '../../images/overview-image.png';
import Educationcard from '../Home/Educationcard';
import { Grid } from "@mui/material";
import './Dashboard.css';
import feemonk from "../../Assets/images/FeeMonk-MonkIllustration.png";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import App1 from './Reciept/App1';

function fmt(n) {
  if (n === null || n === undefined) return '0';
  return Math.round(Number(n)).toLocaleString('en-IN');
}

const closedRecentPool = [
  "Recently closed! Your financial record has been updated to reflect this successful repayment.",
  "Just closed! Great job completing this commitment.",
  "Fresh closure! Your financial record is now impeccable for this loan.",
];
const closedOldPool = [
  "Your loan commitment has been fully met. Your financial discipline is commendable.",
  "Another milestone achieved! This repayment reflects your strong financial habits.",
  "Well done on completing this loan. Your financial standing keeps getting stronger.",
  "Fully repaid. Consistent repayments like this unlock better rates in the future.",
];
function getClosedLoanMessage(item, index) {
  const raw = item.endDate || item.startDate;
  const days = raw ? (Date.now() - new Date(raw)) / 86400000 : 999;
  const pool = days <= 30 ? closedRecentPool : closedOldPool;
  return pool[index % pool.length];
}

export default function Dashboard() {
  const token = sessionStorage.getItem('token');
  const decode = jwtDecode(token);
  const navigate = useNavigate();

  const QUOTES = [
    { text: "A debt-free life is the ultimate financial freedom.", attr: "Financial Wisdom Series" },
    { text: "Invest in your education today; it pays the best dividends tomorrow.", attr: "Financial Wisdom Series" },
    { text: "The secret to financial success is spending less than you earn.", attr: "Financial Wisdom Series" },
    { text: "Small consistent payments build the foundation of a strong credit history.", attr: "Financial Wisdom Series" },
    { text: "Financial discipline today creates the freedom you desire tomorrow.", attr: "Financial Wisdom Series" },
    { text: "Every EMI paid on time is a step closer to your financial goals.", attr: "Financial Wisdom Series" },
    { text: "Your credit score is your financial reputation — protect it.", attr: "Financial Wisdom Series" },
    { text: "Borrowing wisely is the first step toward building lasting wealth.", attr: "Financial Wisdom Series" },
  ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const wisdomQuote = useMemo(() => QUOTES[Math.floor(Math.random() * QUOTES.length)], []);

  const [loans, setLoans] = useState([]);
  const [closedLoans, setClosedLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const sideNav = searchParams.get('tab') || 'dashboard';

  const [expandedLoan, setExpandedLoan] = useState(null);
  const [emiData, setEmiData] = useState({});
  const [emiLoading, setEmiLoading] = useState({});
  const [selectedEmis, setSelectedEmis] = useState({});
  const [emiPage, setEmiPage] = useState({});
  const [appDetails, setAppDetails] = useState([]);
  const [appDetailsLoading, setAppDetailsLoading] = useState(true);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 950);
  const [receiptsModal, setReceiptsModal] = useState(null);
  const [receiptsData, setReceiptsData] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [loanProfiles, setLoanProfiles] = useState({});
  const [docViewer, setDocViewer] = useState({ open: false, url: '', title: '' });
  const [docsExpanded, setDocsExpanded] = useState({});

  const [openForeClose, setOpenForeClose] = useState(false);
  const [mandateModal, setMandateModal] = useState(false);
  const [foreclosureDetails, setForeclosureDetails] = useState({
    amount: null,
    charges: null,
    netAmount: null,
    isWaived: false,
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 950);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const url = `${process.env.REACT_APP_DASHBOARD_URL}/users/basic-info?searchField=${decode?.mobile}`;
    axios
      .get(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setAppDetails(res?.data?.data || []))
      .catch((e) => console.log(e))
      .finally(() => setAppDetailsLoading(false));
  }, []);

  useEffect(() => {
    const url = `${process.env.REACT_APP_DASHBOARD_URL}/loan-repayment/user-loans?userId=${decode?.userId}`;
    axios
      .get(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const all = res?.data?.data || [];
        setLoans(all.filter((i) => i.status === 1 || i.status === 3 || i.status === 5 || i.status === 7));
        setClosedLoans(all.filter((i) => ![1, 3, 4, 5, 7, 8, 9].includes(i.status)));
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false));
  }, []);

  const fetchEmis = (loanId) => {
    if (emiData[loanId]) return;
    setEmiLoading(prev => ({ ...prev, [loanId]: true }));
    const url = `${process.env.REACT_APP_DASHBOARD_URL}/emis/for-loan?loanId=${loanId}`;
    axios.get(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setEmiData(prev => ({ ...prev, [loanId]: res.data.data || [] })))
      .catch(e => console.log(e))
      .finally(() => setEmiLoading(prev => ({ ...prev, [loanId]: false })));
  };

  const fetchLoanProfile = (loanId) => {
    axios.get(`${process.env.REACT_APP_DASHBOARD_URL}/loan-repayment/profile?loanId=${loanId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setLoanProfiles(prev => ({ ...prev, [loanId]: res?.data?.data || {} })))
      .catch(e => console.log(e));
  };

  useEffect(() => {
  if (loans.length > 0 && expandedLoan === null) {
    const firstId = loans[0].loanId;
    setExpandedLoan(firstId);
    fetchEmis(firstId);
  }
  }, [loans]);

  // ── FETCH ALL EMIs FOR ALERT CARD ──
useEffect(() => {
  loans.forEach(l => fetchEmis(l.loanId));
}, [loans]);

  useEffect(() => {
    loans.forEach(l => fetchLoanProfile(l.loanId));
  }, [loans]);

  useEffect(() => {
    closedLoans.forEach(l => fetchEmis(l.loanId));
    closedLoans.forEach(l => fetchLoanProfile(l.loanId));
  }, [closedLoans]);

  const autoInitialized = useRef(new Set());
  useEffect(() => {
    const PAGE_SIZE = 5;
    Object.entries(emiData).forEach(([loanId, emis]) => {
      if (!emis || emis.length === 0) return;
      if (autoInitialized.current.has(loanId)) return;
      const firstUnpaidIdx = emis.findIndex(
        e => e.status != 2 && e.status != 4 && e.status != 7 && e.status != 9
      );
      if (firstUnpaidIdx === -1) return;
      autoInitialized.current.add(loanId);
      const newSel = [];
      for (let i = 0; i <= firstUnpaidIdx; i++) {
        const e = emis[i];
        const isPaid = e.status == 2 || e.status == 4 || e.status == 7 || e.status == 9;
        if (!isPaid) newSel.push(e);
      }
      setSelectedEmis(prev => ({ ...prev, [loanId]: newSel }));
      setEmiPage(prev => ({ ...prev, [loanId]: Math.floor(firstUnpaidIdx / PAGE_SIZE) }));
    });
  }, [emiData]);

  const toggleLoan = (loanId) => {
    if (expandedLoan === loanId) {
      setExpandedLoan(null);
    } else {
      setExpandedLoan(loanId);
      fetchEmis(loanId);
      setEmiPage(prev => ({ ...prev, [loanId]: 0 }));
    }
  };

  const handleEmiCheck = (loanId, emi, index, checked, allEmis) => {
    if (!checked) {
      setSelectedEmis(prev => ({ ...prev, [loanId]: [] }));
    } else {
      const newSel = [];
      for (let i = 0; i <= index; i++) {
        const e = allEmis[i];
        const isPaid = e.status == 2 || e.status == 4 || e.status == 7 || e.status == 9;
        if (!isPaid) newSel.push(e);
      }
      setSelectedEmis(prev => ({ ...prev, [loanId]: newSel }));
    }
  };

  const handleForecloseButtonClick = async (loan) => {
    if (loan?.status === 5) {
      setMandateModal(true);
      return;
    }
    try {
      sessionStorage.setItem("PreCloseLoanId", loan.loanId);

      const res = await fetch(
        `${process.env.REACT_APP_DASHBOARD_URL}/loan-repayment/preclose-amount`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ loanId: loan.loanId })
        }
      );

     const data = await res.json();
      console.log("Pre-close API response:", data);

      const totalAmount = data?.data || 0;
      const foreclosureCharges = data?.preClosureCharges || 0;

      // Principal amount excluding foreclosure charges
      const principalOutstanding =
        totalAmount - foreclosureCharges;

      const isWaived =
        loan.facilityType === 1 ||
        loan.facilityType === 5;

      // Final amount payable
      const netPayable = isWaived
        ? principalOutstanding
        : totalAmount;

      setForeclosureDetails({
        amount: principalOutstanding,
        charges: foreclosureCharges,
        netAmount: netPayable,
        isWaived
      });

      setOpenForeClose(true);

    } catch (err) {
      alert("Error fetching preclose amount");
    }
  };

  const handleForeClose = () => {
    setOpenForeClose(false);
    setForeclosureDetails({
      amount: null,
      charges: null,
      netAmount: null,
      isWaived: false,
    });
  };

  const emiStatusBadge = (status) => {
    const b = { fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, display: 'inline-block', whiteSpace: 'nowrap' };
    if (status == 1) return <span style={{ ...b, background: '#EDE7FF', color: '#6E24E7' }}>UnPaid</span>;
    if (status == 2 || status == 8 || status == 9) return <span style={{ ...b, background: '#ECFDF3', color: '#12B76A' }}>Paid</span>;
    if (status == 3) return <span style={{ ...b, background: '#FFF6E8', color: '#F8A31F' }}>PartlyPaid</span>;
    if (status == 4) return <span style={{ ...b, background: '#ECFDF3', color: '#12B76A' }}>Paid</span>;
    if (status == 5) return <span style={{ ...b, background: '#FEEAEB', color: '#D22129' }}>Overdue</span>;
    if (status == 6) return <span style={{ ...b, background: '#FFF6E8', color: '#F8A31F' }}>Pause</span>;
    if (status == 7) return <span style={{ ...b, background: '#F0F0F0', color: '#595959' }}>Mandate In Progress</span>;
    if (status == 10) return <span style={{ ...b, background: '#F0F0F0', color: '#777' }}>Refunded</span>;
    if (status == 11) return <span style={{ ...b, background: '#ECFDF3', color: '#12B76A' }}>Settled</span>;
    if (status == 12) return <span style={{ ...b, background: '#FEEAEB', color: '#D22129' }}>Written Off</span>;
    if (status == 13) return <span style={{ ...b, background: '#F0F0F0', color: '#777' }}>Opted Out</span>;
    return <span style={{ ...b, background: '#F0F0F0', color: '#777' }}>{status || '-'}</span>;
  };

  const fmtEmiDate = (d) => {
  if (!d || d === 'Invalid date') return '--';

  const parts = d.split('/');

  if (parts.length !== 3) return '--';

  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return `${parts[0]} ${months[parseInt(parts[1]) - 1]} ${parts[2]}`;
};

  const handleStatementDownload = async (loanItem, emis) => {
    const fmtDate = (d) => {
      if (!d) return 'NA';
      const dt = new Date(d);
      if (isNaN(dt)) return d;
      return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${dt.getFullYear()}`;
    };

    try {
      // Fetch full profile (same endpoint as Loandetails.js) to get name, email, address, dates
      const profileRes = await axios.get(
        `${process.env.REACT_APP_DASHBOARD_URL}/loan-repayment/profile?loanId=${loanItem.loanId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const profile = profileRes?.data?.data || loanItem;

      const firstName = profile.firstName || '';
      const lastName = profile.lastName || '';

      const totalDue = (emis || []).reduce((s, e) => s + (e?.remainingAmountToBePaid || 0), 0);
      const totalOutstanding = (emis || []).reduce((s, e) => {
        if (e?.status === 1 || e?.status === 3 || e?.status === 5)
          return s + ((e?.principleAmount || 0) - (e?.paidPrinciple || 0));
        return s;
      }, 0);

      const statementDetails = {
        customerInfo: {
          customerName: firstName + ' ' + lastName,
          loanId: profile.legacyLoanId,
          applicationDate: fmtDate(profile.applideDate),
          disbursalDate: fmtDate(profile.disbursedDate),
          email: profile.email || '',
          closureDate: profile.closureDate ? fmtDate(profile.closureDate) : 'NA',
          address: profile.address,
        },
        loanDetails: {
          lenderName: 'Monk Capital Private Limited',
          loanAmount: `Rs. ${(profile.loanAmount || profile.pricipalAmount || 0).toLocaleString('en-IN')}`,
          tenure: profile.tenure ? `${profile.tenure} months` : 'NA',
          totalDue: `Rs. ${totalDue.toLocaleString('en-IN')}`,
          principalOutstanding: `Rs. ${totalOutstanding.toLocaleString('en-IN')}`,
        },
        statementData: emis,
      };

      const fileName = `Statement_${(firstName + '_' + lastName).replace(/\s+/g, '_')}_${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.pdf`;

      const blob = await pdf(<Statement data={statementDetails} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Could not generate statement. Please try again.');
    }
  };

  const handleResume = async (item) => {
    try {
      const mobile = (() => { try { return JSON.parse(sessionStorage.getItem('eligibility_data') || '{}').mobile || sessionStorage.getItem('mob') || ''; } catch { return ''; } })();
      const navData = { userId: item.userId, applicationId: item.applicationId, mobile };
      sessionStorage.setItem('eligibility_data', JSON.stringify(navData));
      const statusLower = (item.status || '').toLowerCase();
      if (statusLower === 'dropped' || statusLower === 'on hold') {
        const updateRes = await axios.post(
          `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/update-status`,
          { userId: item.userId, applicationId: item.applicationId, status: 'In Review Again', stage: 3 },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (updateRes?.status !== 200) return;
      }
      const stageRes = await axios.get(
        `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/stage?applicationId=${item.applicationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const stage = stageRes?.data?.data?.stage;
      if (stage === 1) { navigate('/eligibility/income', { state: { data: navData } }); return; }
      if (stage === 2 || stage === 3) { navigate('/eligibility/bank-statement', { state: { data: navData } }); return; }
      if (stage === 4) { navigate('/eligibility/waiting', { state: { data: navData, nextRoute: '/eligibility/digilocker' } }); return; }
      if (stage === 5) { navigate('/eligibility/under-review', { state: { data: navData } }); return; }
      if (stage === 8) { navigate('/eligibility/congratulations', { state: { data: navData } }); return; }
      if (stage === 9) { navigate('/eligibility/selfie', { state: { data: navData } }); return; }
      if (stage === 10) { navigate('/eligibility/waiting', { state: { data: navData, fromStage10: true } }); return; }
      if (stage === 11) { navigate('/eligibility/agreement', { state: { data: navData } }); return; }
      if (stage === 12) { navigate('/eligibility/emandate', { state: { data: navData } }); return; }
      if (stage === 18) { navigate('/eligibility/rejected', { state: { data: navData } }); return; }
      if (stage === 24) { navigate('/eligibility/digilocker', { state: { data: navData } }); return; }
      if (stage === 25) { navigate('/eligibility/evaluating', { state: { data: navData } }); return; }
      navigate('/eligibility/pan', { state: { mobile } });
    } catch (e) { console.log('resume error:', e); }
  };

  const handleDocumentDownload = async (path, fileName) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_DASHBOARD_URL}/file/url-content?path=${path}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const fileUrl = res?.data?.data || res?.data?.url || res?.data;
      if (typeof fileUrl === 'string') {
        if (window.innerWidth < 900) {
          window.open(fileUrl, '_blank');
        } else {
          setDocViewer({ open: true, url: fileUrl, title: fileName });
        }
      }
    } catch (e) { console.log(e); }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/eligibility/login');
    window.location.reload();
  };

  const handleApplyNewLoan = () => {
    const tok = sessionStorage.getItem('token');
    if (tok) sessionStorage.setItem('eligibility_auth_token', tok);
    const decoded = jwtDecode(tok || '');
    navigate('/eligibility/pan', {
      state: {
        name: decoded?.name || '',
        mobile: decoded?.mobile || '',
      },
    });
  };

  const appBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'disbursed') return { label: 'DISBURSED', bg: '#ECFDF3', color: '#12B76A' };
    if (s === 'approved') return { label: 'APPROVED', bg: '#EEF4FF', color: '#4B7BF5' };
    if (s === 'under review') return { label: 'UNDER REVIEW', bg: '#EEF4FF', color: '#4B7BF5' };
    if (s === 'rejected') return { label: 'REJECTED', bg: '#FEEAEB', color: '#D22129' };
    if (s === 'dropped') return { label: 'DROPPED', bg: '#F5F5F5', color: '#777' };
    return { label: status || 'IN PROGRESS', bg: '#FFF6E8', color: '#F8A31F' };
  };

  const fmtAppDate = (d) => {
    if (!d) return '--';
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // ── EMI ALERT LOGIC ──
const getEmiAlert = () => {
  for (const loan of loans) {
    const emis = emiData[loan.loanId] || [];

    const mandate = emis.find(e => e.status == 7);
    if (mandate) {
      return { type: 'mandate', emi: mandate, loan };
    }

    const overdue = emis.find(e => e.status == 5);
    if (overdue) {
      return {
        type: 'overdue',
        text: `Your EMI of ₹${fmt(overdue.remainingAmountToBePaid || loan.emi)} is currently overdue. Pay now to avoid charges.`,
        amount: overdue.remainingAmountToBePaid,
        emi: overdue,
        loan: loan
      };
    }

    const upcoming = emis.find(e => e.status == 1);
    if (upcoming) {
      return {
        type: 'upcoming',
        text: `Your next installment of ₹${fmt(upcoming.emiAmount)} is due soon.`,
        amount: upcoming.remainingAmountToBePaid || upcoming.emiAmount,
        emi: upcoming,
        loan: loan
      };
    }
  }
  return null;
};

const emiAlert = getEmiAlert();

 const fetchJsFromCDN = (src, externals) => {
    externals = !externals ? (externals = []) : externals;
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.setAttribute("src", src);
      script.addEventListener("load", () => {
        resolve(
          externals.map((key) => {
            const ext = window[key];
            typeof ext === "undefined" &&
              console.warn(`No external named '${key}' in window`);
            return ext;
          })
        );
      });
      script.addEventListener("error", reject);
      document.body.appendChild(script);
    });
  };

const razorpayCheckoutHandler = async (emis, reqAmount, loanId, token,closureType = 2) => {
      fetchJsFromCDN("https://checkout.razorpay.com/v1/checkout.js", [])
        .then(async (razorp) => {
          const response = await fetch(
            `${process.env.REACT_APP_DASHBOARD_URL}/razorpay-checkout/create-order`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                amount:reqAmount,
                loanId:loanId,
                emis,
                closureType
              }),
            }
          );
          const result = await response.json();
          console.log(result);
          const { id, status, amount: orderAmount, name, mobile } = result.data;  
          console.log(id, status, orderAmount, "---> id, status, amount");
  
          if (status == "created") {
            const options = {
              key: "rzp_live_MmJK5YgAShBjZ3", // Enter the Key ID generated from the Dashboard
              amount: orderAmount, // Amount is in currency subunits. Default currency is INR. Hence, 50000 refers to 50000 paise
              currency: "INR",
              name: "Feemonk (Monk Capital Pvt Ltd)",
              description: sessionStorage.getItem("PreCloseLoanId")?sessionStorage.getItem("PreCloseLoanId"):sessionStorage.getItem("LoanId"),
              image: feemonk,
              order_id: id, //This is a sample Order ID. Pass the id obtained in the response of Step 1
              prefill: {
                name: name,
                // "email": "gaurav.kumar@example.com",
                contact: mobile,
              },
              callback_url:
                "http://apply-backend.feemonk.com/razorpay-checkout/resolve",
              // "notes": {
              //   "address": "2nd Floor, (SOUTHSIDE, Prasanna Hitex, 1-82/2/SUITE B, Manchirevula, Hyderabad, 500089"
              // },
              theme: {
                color: "#f77723",
              },
            };
  
            const paymentObject = new window.Razorpay(options);
            paymentObject.open();
          }
        })
        .catch((err) => console.log(err));
};

const handlePayNow = (loan, sel, selTotal, onMandateBlock) => {
  if (!sel || sel.length === 0) return;
  if (loan?.status === 5) {
    onMandateBlock?.();
    return;
  }
  const emiIds = sel.map(e => e.emiId);
  razorpayCheckoutHandler(emiIds, selTotal, loan.loanId, token);
};

const handleAlertPay = (loan, emi, onMandateBlock) => {
  const amount = emi.remainingAmountToBePaid || emi.emiAmount;
  const emis = [emi.emiId];

  if (loan?.status === 5) {
    onMandateBlock?.();
    return;
  }

  razorpayCheckoutHandler(emis, amount, loan.loanId, token);
};

  /* ── shared content sections ── */
  const ActiveSection = ({ wide = false }) => (
    <section className="db-section">
      <div className="db-section-head">
        <span className="db-section-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" stroke="#D32028" strokeWidth="2" strokeLinejoin="round" fill="#D32028"/>
          </svg>
        </span>
        <h2 className="db-section-title">Active Loans</h2>
      </div>

      {loading ? (
        <p className="db-loading">Loading…</p>
      ) : loans.length === 0 ? (
        <div className="db-active-empty">
          <div className="db-active-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#E5E7EB" strokeWidth="1.5"/>
              <path d="M9 12l2 2 4-4" stroke="#D0D5DD" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <p className="db-active-empty-title">No Active Loans</p>
          <p className="db-active-empty-sub">You don't have any active loans at the moment. Apply for a new loan to get started.</p>
          <button className="db-active-empty-btn db-active-empty-btn--primary" onClick={handleApplyNewLoan}>Apply for a New Loan →</button>
        </div>
      ) : wide && !isMobile ? (
        <div className="db-loan-accordion">
          {loans.map((loanItem, i) => {
            const loanId = loanItem.loanId;
            const isExp = expandedLoan === loanId;
            const emis = emiData[loanId] || [];
            const isEmiLoading = emiLoading[loanId];
            const sel = selectedEmis[loanId] || [];
            const selTotal = sel.reduce((sum, e) => sum + (e.remainingAmountToBePaid || 0), 0);
            const PAGE_SIZE = 5;
            const page = emiPage[loanId] || 0;
            const pagedEmis = emis.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
            const totalPages = Math.ceil(emis.length / PAGE_SIZE);
            const total = emis.length;
            const paid = emis.filter(e => e.status == 2 || e.status == 4 || e.status == 9).length;
            const percent = total ? Math.round((paid / total) * 100) : 0;
            return (
              <div key={i} className="db-acc-card">
                <div className="db-acc-header" onClick={() => toggleLoan(loanId)}>
                  <div className="db-acc-loan-meta">
                    <div>
                      <p className="db-acc-meta-label">LOAN ID</p>
                      <p className="db-acc-meta-val">{loanItem.legacyLoanId || loanId}</p>
                    </div>
                    <div>
                      <p className="db-acc-meta-label">PRINCIPAL</p>
                      <p className="db-acc-meta-val">₹{fmt(loanItem.pricipalAmount)}</p>
                    </div>
                    <div>
                      <p className="db-acc-meta-label">EMI AMOUNT</p>
                      <p className="db-acc-meta-val">₹{fmt(loanItem.emi)}</p>
                    </div>
                    <div className="db-start-date-row">
                      <div>
                        <p className="db-acc-meta-label">START DATE</p>
                        <p className="db-acc-meta-val">{loanItem.startDate || '--'}</p>
                      </div>

                      {isMobile && (
                        <button
                          className="db-statement-btn-mob"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          Statement
                        </button>
                      )}
                    </div>
                    
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 'auto', paddingLeft: 16 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ transform: isExp ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
                      <polyline points="6 9 12 15 18 9" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>

                {isExp && (
                  <div className="db-acc-body">
                    <div className="db-emi-section-head">
                      
                      <h3 className="db-emi-section-title">EMI Payment History</h3>

                      <div style={{ display: 'flex', gap: 10 }}>
                        <p className="db-emi-outline-btn" onClick={(e) => { e.stopPropagation(); goToReceipts(loanItem); }}>
                          Receipts
                        </p>

                        <p className="db-emi-outline-btn" onClick={(e) => { e.stopPropagation(); handleStatementDownload(loanItem, emiData[loanItem.loanId] || []); }}>
                          Statement
                        </p>
                      </div>
                    </div>
                    {!isMobile && (
<div className="db-emi-header">

                      {/* LEFT */}
                      <div className="db-emi-left">

                        <div className="db-emi-progress-wrap">

                          <div className="db-emi-progress-top">
                            <span style={{ color: 'black' }}>EMI Progress</span>
                            <span style={{ color: 'black' }}>{percent}%</span>
                          </div>

                          <div className="db-emi-progress-bar">
                            <div
                              className="db-emi-progress-fill"
                              style={{
                                width: `${percent}%`,
                                background:
                                  percent >= 67
                                    ? '#22c55e'
                                    : percent >= 34
                                      ? '#f97316'
                                      : '#D32028'
                              }}
                            />
                          </div>

                          <div className="db-emi-progress-text">
                            {paid} of {total} EMIs paid
                          </div>

                        </div>

                      </div>

                      {/* RIGHT */}
                      <div className="db-emi-right">

                        <span className="db-emi-total">
                          {sel.length} SELECTED | Total Payable: <strong>₹{fmt(selTotal)}</strong>
                        </span>

                        <button
                          className="db-emi-paynow-btn"
                          disabled={sel.length === 0}
                          onClick={() => handlePayNow(loanItem, sel, selTotal, () => setMandateModal(true))}
                        >
                          Pay Now
                        </button>

                      </div>

                    </div>
                    )}
                    
                    {/* <p className="db-emi-disclaimer">The total payable amount may vary if payments are made after the due date as additional charges may apply.</p> */}

                    {isEmiLoading ? (
                      <p className="db-loading">Loading EMIs…</p>
                    ) : emis.length === 0 ? (
                      <p className="db-empty">No EMI data available</p>
                    ) : isMobile ?(
                      <div className="db-emi-mobile-list">
                            {emis.map((emi, idx) => {
                              const isPaid = emi.status == 2 || emi.status == 4 || emi.status == 9;
                              const isSelected = sel.some(e => e.emiId === emi.emiId);

                              return (
                                <div key={idx} className={`db-emi-mob-card ${!isPaid ? 'pending' : ''}`}>

                                  {/* checkbox */}
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={isPaid}
                                    onChange={(e) => handleEmiCheck(loanId, emi, idx, e.target.checked, emis)}
                                  />

                                  <div className="db-emi-mob-content">

                                    <div className="db-emi-mob-top">
                                      <p>
                                        {isPaid
                                          ? `Paid: ${fmtEmiDate(emi.dueDate)}`
                                          : `Next Due: ${fmtEmiDate(emi.dueDate)}`
                                        }
                                      </p>

                                      {emiStatusBadge(emi.status)}
                                    </div>

                                    <div className="db-emi-mob-bottom">
                                      <div>
                                        <span>EMI Amount</span>
                                        <p>₹{fmt(emi.emiAmount)}</p>
                                      </div>

                                      <div>
                                        <span>Charges</span>
                                        <p style={{ color: '#D32028' }}>
                                          ₹{fmt(
                                            (emi.bounceCharges || 0) +
                                            (emi.overdueCharges || 0) -
                                            (emi.bounceDiscount || 0) -
                                            (emi.penaltyDiscount || 0)
                                          )}
                                        </p>
                                      </div>
                                    </div>

                                  </div>
                                </div>
                              );
                            })}
                          </div>
                    ) : (
                      <div className="db-emi-table-wrap">
                        <table className="db-emi-table">
                          <thead>
                            <tr>
                              <th></th>
                              <th>Due Date</th>
                              <th>EMI Amount</th>
                              <th>Charges</th>
                              <th>Paid Amount</th>
                              <th>Paid Date</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pagedEmis.map((emi, idx) => {
                              const isPaid = emi.status == 2 || emi.status == 4 || emi.status == 7 || emi.status == 9;
                              const isSelected = sel.some(e => e.emiId === emi.emiId);
                              return (
                                <tr key={idx} className="db-emi-row">
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      disabled={isPaid}
                                      style={{ accentColor: '#D32028', cursor: isPaid ? 'not-allowed' : 'pointer' }}
                                      onChange={(e) => handleEmiCheck(loanId, emi, page * PAGE_SIZE + idx, e.target.checked, emis)}
                                    />
                                  </td>
                                  <td>{fmtEmiDate(emi.dueDate)}</td>
                                  <td>₹{fmt(emi.emiAmount)}</td>
                                  <td style={{ color: emi.bounceCharges > 0 ? '#D32028' : null}}>₹{fmt(
                                    (emi.bounceCharges || 0) +
                                    (emi.overdueCharges || 0) -
                                    (emi.bounceDiscount || 0) -
                                    (emi.penaltyDiscount || 0)
                                  )}</td>
                                   <td style={{ color: emi.bounceCharges > 0 ? '#D32028' : '#1a1a1a' }}>
                                    ₹{emi.repaidEmiAmount ? fmt(emi.repaidEmiAmount) : fmt(0)}
                                  </td>
                                  <td>{fmtEmiDate(emi.emiRepaidDate)}</td>
                                  <td>{emiStatusBadge(emi.status)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div className="db-emi-pagination">
                          <span>Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total} EMIs</span>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              className="db-emi-page-btn"
                              disabled={page === 0}
                              onClick={() => setEmiPage(prev => ({ ...prev, [loanId]: page - 1 }))}
                            >&#8249;</button>
                            <button
                              className="db-emi-page-btn"
                              disabled={page >= totalPages - 1}
                              onClick={() => setEmiPage(prev => ({ ...prev, [loanId]: page + 1 }))}
                            >&#8250;</button>
                          </div>
                        </div>
                      </div>
                    )}
                    {isMobile && (
                      <div className="db-emi-sticky-bar">
                        <div>
                          <p>{sel.length} Item Selected</p>
                          <strong>Total: ₹{fmt(selTotal)}</strong>
                        </div>

                        <button
                          disabled={sel.length === 0}
                          onClick={() => handlePayNow(loanItem, sel, selTotal, () => setMandateModal(true))}
                        >
                          Pay Now →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="db-mob-active-list">
          {loans.map((loanItem, i) => {
            const loanId = loanItem.loanId;
            const emis = emiData[loanId] || [];
            const sel = selectedEmis[loanId] || [];
            const isEmiLoading = emiLoading[loanId];
            return (
              <div key={i} className="db-mob-loan-block">
                <div className="db-mob-loan-card">
                  <div className="db-mob-loan-top">
                    <div>
                      <p className="db-mob-loan-id-label">LOAN ID</p>
                      <p className="db-mob-loan-id">{loanItem.legacyLoanId || loanId}</p>
                    </div>
                    <span className="db-mob-loan-active-badge">Active</span>
                  </div>
                  <div className="db-mob-loan-grid">
                    <div>
                      <p className="db-mob-loan-grid-label">Principal Amount</p>
                      <p className="db-mob-loan-grid-val db-mob-loan-red">₹{fmt(loanItem.pricipalAmount)}</p>
                    </div>
                    <div>
                      <p className="db-mob-loan-grid-label">EMI Amount</p>
                      <p className="db-mob-loan-grid-val db-mob-loan-red">₹{fmt(loanItem.emi)}</p>
                    </div>
                    <div>
                      <p className="db-mob-loan-grid-label">Start Date</p>
                      <p className="db-mob-loan-grid-val">{loanItem.startDate || '--'}</p>
                    </div>
                  </div>
                  <div className="db-mob-loan-actions">
                    <button className="db-mob-stmt-btn" onClick={() => handleStatementDownload(loanItem, emiData[loanId] || [])}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', marginRight: 5 }}>
                        <path d="M12 16l-6-6h4V4h4v6h4l-6 6z" fill="currentColor"/>
                        <path d="M4 20h16v-2H4v2z" fill="currentColor"/>
                      </svg>
                      Loan Statement
                    </button>
                    <button className="db-mob-stmt-btn" onClick={() => goToReceipts(loanItem)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', marginRight: 5 }}>
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="9" y1="13" x2="15" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                        <line x1="9" y1="17" x2="15" y2="17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                      </svg>
                      Receipts
                    </button>
                  </div>
                </div>
                <div className="db-mob-emi-section">
                  <div className="db-mob-emi-head">
                    <h3 className="db-mob-emi-title">EMI {emis.filter(e => e.status == 2 || e.status == 4 || e.status == 8 || e.status == 9).length}/{emis.length} Completed</h3>
                    {/* <span className="db-mob-emi-filter">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', marginRight: 3 }}>
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" stroke="#555" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Filter
                    </span> */}
                  </div>
                  {isEmiLoading ? (
                    <p className="db-loading">Loading EMIs…</p>
                  ) : emis.length === 0 ? (
                    <p className="db-empty">No EMI data</p>
                  ) : (
                    <div className="db-mob-emi-list">
                      {emis.map((emi, eidx) => {
                        const isPaid = emi.status == 2 || emi.status == 4 || emi.status == 9;
                        const isOverdue = emi.status == 5;
                        const isSelected = sel.some(e => e.emiId === emi.emiId);
                        return (
                          <div key={eidx} style={{
                              border: '1px solid #eee',
                              borderRadius: '10px',
                              padding: '12px',
                              marginBottom: '12px',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                              background: '#fff'
                            }}>
                            <Grid container alignItems="center" >
                              <Grid item xs={1}>
                                
                            <input
                                type="checkbox"
                                checked={isPaid ? false : isSelected}
                                disabled={isPaid}
                                style={{ accentColor: '#D32028', cursor: isPaid ? 'not-allowed' : 'pointer', width: 16, height: 16 }}
                                onChange={(e) => handleEmiCheck(loanId, emi, eidx, e.target.checked, emis)}
                              />
                              </Grid>
                              <Grid item xs={11}>
                            <div>
                            {isPaid ? (
                              <>
                              <Grid container alignItems="center" justifyContent="space-between" style={{ marginBottom: 8 }}>
                              <Grid item xs={6} style={{fontSize:'12px',color: "black",fontFamily: "'Inter-Regular', sans-serif"}}>
                                Due: {fmtEmiDate(emi.dueDate)}
                              </Grid>
                              <Grid item xs={6} style={{fontSize:'12px',color: "black",fontFamily: "'Inter-Regular', sans-serif",textAlign: 'right' }}>
                                {emiStatusBadge(emi.status)}
                              </Grid>
                            </Grid>
                              <Grid container alignItems="center" justifyContent="space-between" style={{ marginBottom: 8 }}>
                                <Grid item xs={6} style={{fontSize:'12px',color: "black",fontFamily: "'Inter-Regular', sans-serif"}}>
                                  Paid: {fmtEmiDate(emi.emiRepaidDate)}
                                </Grid>
                                <Grid item xs={6} style={{fontSize:'12px',color: "black",fontFamily: "'Inter-Regular', sans-serif",textAlign: 'right' }}>
                                  Paid Amt: ₹{fmt(emi.repaidEmiAmount || 0)}
                                </Grid>
                              </Grid>
                              </>
                            ) : (
                              <>
                             <Grid container alignItems="center" justifyContent="space-between" style={{ marginBottom: 8 }}>
                              <Grid item xs={6} style={{fontSize:'12px',color: "black",fontFamily: "'Inter-Regular', sans-serif"}}>
                                Due: {fmtEmiDate(emi.dueDate)}
                              </Grid>
                              <Grid item xs={6} style={{fontSize:'12px',color: "black",fontFamily: "'Inter-Regular', sans-serif",textAlign: 'right' }}>
                                {emiStatusBadge(emi.status)}
                              </Grid>
                            </Grid>
                              <Grid container alignItems="center" justifyContent="space-between" style={{ marginBottom: 8 }}>
                                   
                                <Grid item xs={6} style={{fontSize:'12px',color: "black",fontFamily: "'Inter-Regular', sans-serif"}}>
                                  Emi Amt: ₹{fmt(emi.emiAmount)}
                                </Grid>
                                <Grid item xs={6} style={{fontSize:'12px',color: "black",fontFamily: "'Inter-Regular', sans-serif",textAlign: 'right' }}>
                                  Total Chrgs: ₹{fmt(emi.repaidEmiAmount || 0)}
                                </Grid>
                              </Grid>
                              </>
                            )}
                            </div>
                            </Grid>
                            </Grid>
                          </div>
                          
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {(() => {
            const count = Object.values(selectedEmis).reduce((s, arr) => s + arr.length, 0);
            if (count === 0) return null;
            const total = Object.values(selectedEmis).reduce((s, arr) => s + arr.reduce((t, e) => t + (e.remainingAmountToBePaid || 0), 0), 0);
            const loanWithSel = loans.find(l => (selectedEmis[l.loanId] || []).length > 0);
            return (
              <div className="db-mob-pay-bar">
                <div >
                  <p className="db-mob-pay-bar-count">{count}{count > 1 ? '' : ''} Selected</p>
                  <p className="db-mob-pay-bar-total">Total: ₹{fmt(total)}</p>
                </div>
                <div className="db-mob-pay-bar-btn" onClick={() => loanWithSel && handlePayNow(loanWithSel, selectedEmis[loanWithSel.loanId] || [], total, () => setMandateModal(true))}>
                  Pay Now &gt;
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {!loading && loans.length > 0 && (
        <div className="db-credit-banner">
          <div className="db-credit-banner-text">
            <div className="db-credit-banner-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L13.8 8.2H20.3L15 12.1L16.9 18.3L12 14.4L7.1 18.3L9 12.1L3.7 8.2H10.2L12 2Z" fill="#E8A84A"/>
              </svg>
              Credit Score Impact
            </div>
            <p className="db-credit-banner-body">
              Timely repayment of your EMIs is the single most critical factor in building a robust credit profile. Maintaining a consistent payment history can improve your score, unlocking lower interest rates for future borrowing.
            </p>
          </div>
          <div className="db-credit-gauge">
            <svg width="110" height="80" viewBox="0 0 110 80">
              {/* background arc */}
              <path d="M10 75 A45 45 0 0 1 100 75" stroke="#4A3030" strokeWidth="10" fill="none" strokeLinecap="round"/>
              {/* red zone */}
              <path d="M10 75 A45 45 0 0 1 30 35" stroke="#C0392B" strokeWidth="10" fill="none" strokeLinecap="round"/>
              {/* orange zone */}
              <path d="M30 35 A45 45 0 0 1 55 24" stroke="#E67E22" strokeWidth="10" fill="none" strokeLinecap="round"/>
              {/* yellow zone */}
              <path d="M55 24 A45 45 0 0 1 80 35" stroke="#F1C40F" strokeWidth="10" fill="none" strokeLinecap="round"/>
              {/* green zone */}
              <path d="M80 35 A45 45 0 0 1 100 75" stroke="#27AE60" strokeWidth="10" fill="none" strokeLinecap="round"/>
              {/* needle */}
              <line x1="55" y1="72" x2="26" y2="42" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
              {/* center dot */}
              <circle cx="55" cy="72" r="5" fill="#fff"/>
              <circle cx="55" cy="72" r="2.5" fill="#2B1D1D"/>
              {/* labels */}
              <text x="6" y="76" fill="#aaa" fontSize="7" fontFamily="sans-serif">300</text>
              <text x="44" y="18" fill="#aaa" fontSize="7" fontFamily="sans-serif">600</text>
              <text x="92" y="76" fill="#aaa" fontSize="7" fontFamily="sans-serif">900</text>
            </svg>
          </div>
        </div>
      )}
    </section>
  );

  const ApplicationsSection = () => (
    <section className="db-section db-section--apps">
      <div className="db-section-head">
        <span className="db-section-icon">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#888" strokeWidth="1.8" strokeLinejoin="round"/>
            <polyline points="14 2 14 8 20 8" stroke="#888" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="8" y1="13" x2="16" y2="13" stroke="#888" strokeWidth="1.8" strokeLinecap="round"/>
            <line x1="8" y1="17" x2="12" y2="17" stroke="#888" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </span>
        <h2 className="db-section-title">Applications</h2>
      </div>

      
      {emiAlert && (
        <div className="db-emi-alert-wrapper">
        <div className={`db-emi-alert ${emiAlert.type}`}>

          <div className="db-emi-alert-left">
            <div className="db-emi-alert-icon">
              {emiAlert.type === 'mandate' ? '🏦' : '⚠️'}
            </div>

            <div>
              <p className="db-emi-alert-title">
                {emiAlert.type === 'overdue'
                  ? 'Action Required: EMI Overdue'
                  : emiAlert.type === 'mandate'
                  ? 'Mandate Presented'
                  : 'Upcoming EMI Reminder'}
              </p>

              <p className="db-emi-alert-text">
                {emiAlert.type === 'upcoming'
                  ? <>Your next installment of <strong>₹{fmt(emiAlert.emi.emiAmount)}</strong> is due{emiAlert.emi.dueDate ? <> on <strong>{fmtEmiDate(emiAlert.emi.dueDate)}</strong></> : ' soon'}.</>
                  : emiAlert.type === 'overdue'
                  ? <>Your EMI of <strong>₹{fmt(emiAlert.emi.remainingAmountToBePaid || emiAlert.loan.emi)}</strong>{emiAlert.emi.dueDate ? <> due on <strong>{fmtEmiDate(emiAlert.emi.dueDate)}</strong></> : ''} is <span style={{ color: '#D32028', fontWeight: 700 }}>overdue</span>.</>
                  : emiAlert.type === 'mandate'
                  ? <>Your mandate has been presented to the bank. Payments are not allowed until the mandate process is complete.</>
                  : emiAlert.text}
              </p>
            </div>
          </div>

          {emiAlert.type !== 'mandate' && (
            <button className="db-emi-alert-btn" onClick={() => handleAlertPay(emiAlert.loan, emiAlert.emi, () => setMandateModal(true))}>
              Pay EMI →
            </button>
          )}

        </div>
        </div>
      )}
      {appDetailsLoading ? (
        <p className="db-loading">Loading…</p>
      ) : appDetails.length === 0 ? (
        <div className="db-active-empty">
          <div className="db-active-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#D0D5DD" strokeWidth="1.5" strokeLinejoin="round"/>
              <polyline points="14 2 14 8 20 8" stroke="#D0D5DD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="8" y1="13" x2="16" y2="13" stroke="#D0D5DD" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="8" y1="17" x2="12" y2="17" stroke="#D0D5DD" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <p className="db-active-empty-title">No Applications</p>
          <p className="db-active-empty-sub">You haven't submitted any loan applications yet. Your applications will appear here once submitted.</p>
          <button className="db-active-empty-btn" onClick={() => setSearchParams({ tab: 'dashboard' })}>Go to Dashboard</button>
        </div>
      ) : (
        <div className="db-app-list">
          {appDetails.map((item, i) => {
            const b = appBadge(item.status);
            const badgeStyle = { fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, display: 'inline-block', background: b.bg, color: b.color, whiteSpace: 'nowrap' };
            return (
              <div key={i} className="db-app-card db-app-card--app">
                {/* Desktop flat row */}
                <div className="db-app-meta">
                  <div>
                    <p className="db-acc-meta-label">APPLICATION ID</p>
                    <p className="db-acc-meta-val">{item.applicationId || '--'}</p>
                  </div>
                  <div>
                    <p className="db-acc-meta-label">APPLIED DATE</p>
                    <p className="db-acc-meta-val">{fmtAppDate(item.appliedOn)}</p>
                  </div>
                  <div>
                    <p className="db-acc-meta-label">LOAN AMOUNT</p>
                    <p className="db-acc-meta-val">{item.courseFees ? `₹${fmt(item.courseFees)}` : '--'}</p>
                  </div>
                  <div>
                    <p className="db-acc-meta-label">STATUS</p>
                    <span style={badgeStyle}>{b.label}</span>
                  </div>
                  {loans.length === 0 && !['disbursed', 'rejected', 'in submission', 'in disbursement', 'pending disbursement'].includes((item.status || '').toLowerCase()) && (
                    <button className="db-app-resume-btn" onClick={() => handleResume(item)}>
                      Resume
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '5px', verticalAlign: 'middle' }}>
                        <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                </div>
                {/* Mobile card */}
                <div className="db-app-mob-card">
                  <div className="db-app-mob-top">
                    <p className="db-app-mob-id">{item.applicationId || '--'}</p>
                    <span style={badgeStyle}>{b.label}</span>
                  </div>
                  <div className="db-app-mob-bottom">
                    <div>
                      <p className="db-app-mob-label">APPLICATION DATE</p>
                      <p className="db-app-mob-date">{fmtAppDate(item.appliedOn)}</p>
                    </div>
                    <p className="db-app-mob-amount">{item.courseFees ? `₹${fmt(item.courseFees)}` : '--'}</p>
                  </div>
                  {loans.length === 0 && !['disbursed', 'rejected', 'in submission', 'in disbursement', 'pending disbursement'].includes((item.status || '').toLowerCase()) && (
                    <button className="db-app-resume-btn" onClick={() => handleResume(item)}>
                      Resume
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '5px', verticalAlign: 'middle' }}>
                        <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* ── Info cards ── */}
      <div className="db-info-cards">
        {/* Need Assistance */}
        
        <div className="db-info-card db-info-card--subtle">
          <div className="db-info-card-img">
            <img src={contactSupport} alt="Need Assistance" />
          </div>
          <div className="db-info-card-body-wrap">
            <div className="db-mob-info-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.88 9.77a19.79 19.79 0 01-3.07-8.68A2 2 0 012.78 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.75a16 16 0 006.29 6.29l1.1-1.1a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h4 className="db-info-card-title db-info-card-title--light">Need Assistance?</h4>
            <div className="db-info-contact-row">
              <div className="db-info-contact-item">
                <span className="db-info-contact-label">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', marginRight: 4 }}>
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.88 9.77a19.79 19.79 0 01-3.07-8.68A2 2 0 012.78 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.75a16 16 0 006.29 6.29l1.1-1.1a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="#aaa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Call us
                </span>
                <p className="db-info-contact-val db-info-contact-val--neutral">+91 95131 68846</p>
              </div>
              <div className="db-info-contact-item">
                <span className="db-info-contact-label">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', marginRight: 4 }}>
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#aaa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="22,6 12,13 2,6" stroke="#aaa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Email us
                </span>
                <p className="db-info-contact-val db-info-contact-val--red">hello@feemonk.com</p>
              </div>
            </div>
          </div>
        </div>

        {/* Borrowing Wisdom */}
        <div className="db-info-card db-info-card--subtle">
          <div className="db-info-card-img">
            <img src={financialWisdom} alt="Borrowing Wisdom" />
          </div>
          <div className="db-info-card-body-wrap">
            <div className="db-mob-info-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h4 className="db-info-card-title db-info-card-title--light">Borrowing Wisdom</h4>
            <p className="db-info-card-body db-info-card-body--light">
              A lower debt-to-income ratio makes you a more attractive candidate for premium interest rates.
            </p>
          </div>
        </div>

        {/* Privacy First */}
        <div className="db-info-card db-info-card--subtle">
          <div className="db-info-card-img">
            <img src={privacyFirst} alt="Privacy First" />
          </div>
          <div className="db-info-card-body-wrap">
            <div className="db-mob-info-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h4 className="db-info-card-title db-info-card-title--light">Privacy First</h4>
            <p className="db-info-card-body db-info-card-body--light">
              Your financial data is encrypted and protected by bank-grade security protocols.
            </p>
          </div>
        </div>
      </div>

      {/* ── EducationCard redirect side-effect ── */}
      {(() => {
        const inSubmissionApp = appDetails.find(a => a.status === 'In Submission');
        return inSubmissionApp ? (
          <Educationcard application={inSubmissionApp} newUser={true} />
        ) : null;
      })()}


    </section>
  );

  const goToReceipts = async (item) => {
    sessionStorage.setItem('LegacyLoanId', item.legacyLoanId || item.loanId);
    sessionStorage.setItem('name', decode?.name || '');
    setReceiptsData([]);
    setReceiptsModal(item);
    setReceiptsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_DASHBOARD_URL}/loan-repayment/all-repayments?loanId=${item.loanId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReceiptsData(res?.data?.data || []);
    } catch (e) {
      setReceiptsData([]);
    }
    setReceiptsLoading(false);
  };

  const ClosedSection = () => (
    <section className="db-section">
      <div className="db-section-head">
        <span className="db-section-icon">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#888" strokeWidth="2"/>
            <polyline points="12 6 12 12 16 14" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
        <h2 className="db-section-title">Closed Loans</h2>
      </div>

      {loading ? (
        <p className="db-loading">Loading…</p>
      ) : closedLoans.length === 0 ? (
        <div className="db-active-empty">
          <div className="db-active-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#E5E7EB" strokeWidth="1.5"/>
              <polyline points="12 6 12 12 16 14" stroke="#D0D5DD" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <p className="db-active-empty-title">No Closed Loans</p>
          <p className="db-active-empty-sub">You don't have any closed loans yet. Once a loan is fully repaid it will appear here.</p>
          <button className="db-active-empty-btn" onClick={() => setSearchParams({ tab: 'dashboard' })}>Go to Dashboard</button>
        </div>
      ) : (
        <>
          <div className="db-cl-list">
            {closedLoans.map((item, i) => (
              <div key={i} className="db-cl-card">
                {/* Celebratory header */}
                <div className="db-cl-header">
                  <div className="db-cl-trophy">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                      <path d="M8 21h8M12 17v4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 3h10v6a5 5 0 01-10 0V3z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 5H4a1 1 0 00-1 1v1a4 4 0 004 4h1M17 5h3a1 1 0 011 1v1a4 4 0 01-4 4h-1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="db-cl-header-text">
                    <h3 className="db-cl-title">Loan Successfully Closed</h3>
                    <p className="db-cl-subtitle">{getClosedLoanMessage(item, i)}</p>
                  </div>
                </div>

                {/* Stats + Actions row — all 5 cells equal flex */}
                <div className="db-cl-stats">
                  <div className="db-cl-stat">
                    <p className="db-cl-stat-label">CLOSURE DATE</p>
                    <p className="db-cl-stat-val">{item.endDate || item.lastEmiDate || item.startDate || '--'}</p>
                  </div>
                  <div className="db-cl-divider" />
                  <div className="db-cl-stat">
                    <p className="db-cl-stat-label">LOAN ID</p>
                    <p className="db-cl-stat-val">{item.legacyLoanId || item.loanId || '--'}</p>
                  </div>
                  <div className="db-cl-divider" />
                  <div className="db-cl-stat">
                    <p className="db-cl-stat-label">LOAN AMOUNT</p>
                    <p className="db-cl-stat-val">{item.pricipalAmount ? `₹${fmt(item.pricipalAmount)}` : '--'}</p>
                  </div>
                  <div className="db-cl-divider" />
                  <div className="db-cl-actions-pair">
                    <div className="db-cl-stat db-cl-stat--action">
                      <button className="db-cl-btn-primary" onClick={() => handleStatementDownload(item, emiData[item.loanId] || [])}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: 6 }}>
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <polyline points="7 10 12 15 17 10" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <line x1="12" y1="15" x2="12" y2="3" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Loan Statement
                      </button>
                    </div>
                    <div className="db-cl-divider" />
                    <div className="db-cl-stat db-cl-stat--action">
                      <button className="db-cl-btn-secondary" onClick={() => goToReceipts(item)}>Receipts</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Info cards */}
          <div className="db-info-cards" style={{ marginTop: 28 }}>
            {/* Credit Achievement */}
            <div className="db-info-card db-info-card--subtle">
              <div className="db-info-card-img">
                <img src={overviewImage} alt="Credit Achievement" />
              </div>
              <div className="db-info-card-body-wrap">
                <div className="db-mob-info-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="22 4 12 14.01 9 11.01" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h4 className="db-info-card-title db-info-card-title--light">Credit Achievement</h4>
                <p className="db-info-card-body db-info-card-body--light">
                  Successfully closing your loan reflects strong financial discipline. This positive repayment history can help improve your credit score and unlock better opportunities ahead.
                </p>
              </div>
            </div>

            {/* Plan Your Next Step */}
            <div className="db-info-card db-info-card--subtle">
              <div className="db-info-card-img">
                <img src={financialWisdom} alt="Plan Your Next Step" />
              </div>
              <div className="db-info-card-body-wrap">
                <div className="db-mob-info-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="3" width="20" height="14" rx="2" stroke="#D32028" strokeWidth="1.8"/>
                    <line x1="8" y1="21" x2="16" y2="21" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round"/>
                    <line x1="12" y1="17" x2="12" y2="21" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </div>
                <h4 className="db-info-card-title db-info-card-title--light">Plan Your Next Step</h4>
                <p className="db-info-card-body db-info-card-body--light">
                  With your loan fully repaid, consider channelling those EMI savings into an emergency fund or investment to build lasting financial security.
                </p>
              </div>
            </div>

            {/* Need Assistance */}
            <div className="db-info-card db-info-card--subtle">
              <div className="db-info-card-img">
                <img src={contactSupport} alt="Need Assistance" />
              </div>
              <div className="db-info-card-body-wrap">
                <div className="db-mob-info-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.88 9.77a19.79 19.79 0 01-3.07-8.68A2 2 0 012.78 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.75a16 16 0 006.29 6.29l1.1-1.1a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h4 className="db-info-card-title db-info-card-title--light">Need Assistance?</h4>
                <div className="db-info-contact-row">
                  <div className="db-info-contact-item">
                    <span className="db-info-contact-label">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', marginRight: 4 }}>
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.88 9.77a19.79 19.79 0 01-3.07-8.68A2 2 0 012.78 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.75a16 16 0 006.29 6.29l1.1-1.1a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="#aaa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Call us
                    </span>
                    <p className="db-info-contact-val db-info-contact-val--neutral">+91 95131 68846</p>
                  </div>
                  <div className="db-info-contact-item">
                    <span className="db-info-contact-label">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', marginRight: 4 }}>
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#aaa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="22,6 12,13 2,6" stroke="#aaa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Email us
                    </span>
                    <p className="db-info-contact-val db-info-contact-val--red">hello@feemonk.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );

  const DashboardSection = () => {
    let alertData = null;
    if (!loading) {
      for (const loan of loans) {
        const emis = emiData[loan.loanId] || [];
        const overdue = emis.find(e => e.status == 5);
        if (overdue) { alertData = { type: 'overdue', loan, emi: overdue }; break; }
        const upcoming = emis.find(e => e.status == 1);
        if (upcoming) { alertData = { type: 'upcoming', loan, emi: upcoming }; break; }
      }
    }

    const firstLoan = loans[0];
    const isOverdue = alertData?.type === 'overdue';

    let badge, titleLg, titleSm, desc;
    if (alertData?.type === 'overdue') {
      badge = 'ACTION REQUIRED';
      titleLg = 'EMI Overdue';
      titleSm = `EMI Overdue: ₹${fmt(alertData.emi.remainingAmountToBePaid || alertData.loan.emi)}`;
      desc = `Your EMI of ₹${fmt(alertData.emi.remainingAmountToBePaid || alertData.loan.emi)} is currently overdue. Pay now to avoid penal interest.`;
    } else if (alertData?.type === 'upcoming') {
      const dueStr = alertData.emi.dueDate ? ` on ${fmtEmiDate(alertData.emi.dueDate)}` : ' soon';
      badge = 'SCHEDULED REPAYMENT';
      titleLg = 'Upcoming EMI Reminder';
      titleSm = `Upcoming EMI: ₹${fmt(alertData.emi.emiAmount)}`;
      desc = <>Your next installment of <strong>₹{fmt(alertData.emi.emiAmount)}</strong> is due<strong>{dueStr}</strong>.</>;
    } else if (!loading && loans.length > 0) {
      badge = 'ACTIVE LOANS';
      titleLg = 'Your Loans Are Active';
      titleSm = `${loans.length} Active Loan${loans.length > 1 ? 's' : ''}`;
      desc = `You have ${loans.length} active loan${loans.length > 1 ? 's' : ''}. Stay on top of your EMI schedule to build a strong credit history and unlock better rates.`;
    } else if (!loading && closedLoans.length > 0) {
      badge = 'LOAN CLOSED';
      titleLg = 'Well Done!';
      titleSm = `Loan${closedLoans.length > 1 ? 's' : ''} Fully Repaid`;
      desc = `You have successfully repaid ${closedLoans.length > 1 ? 'all your loans' : 'your loan'}. Your financial discipline is commendable — keep building a strong credit profile for future opportunities.`;
    } else if (!loading && appDetails.length > 0) {
      badge = 'APPLICATION IN PROGRESS';
      titleLg = 'Application Under Review';
      titleSm = `${appDetails.length} Application${appDetails.length > 1 ? 's' : ''} Submitted`;
      desc = "Your education loan application is being reviewed by our team. Please check back soon for updates.";
    } else if (!loading) {
      badge = 'EDUCATION LOANS';
      titleLg = 'Fund Your Future Today';
      titleSm = 'Apply for a Loan';
      desc = 'Take the next step towards your academic goals with a Feemonk education loan. Quick approvals, competitive rates, and flexible repayment options.';
    } else {
      badge = 'WELCOME';
      titleLg = 'Welcome to Your Dashboard';
      titleSm = 'Dashboard';
      desc = 'Manage your loans, track repayments, and stay on top of your finances — all in one place.';
    }

    // Get last 3 paid EMIs for Payment History — active first, fall back to closed
    const paidStatuses = [2, 3, 4, 8, 9];
    let allPaidEmis = [];
    for (const loan of loans) {
      const emis = emiData[loan.loanId] || [];
      allPaidEmis.push(...emis.filter(e => paidStatuses.includes(e.status)));
    }
    if (allPaidEmis.length === 0) {
      for (const loan of closedLoans) {
        const emis = emiData[loan.loanId] || [];
        allPaidEmis.push(...emis.filter(e => paidStatuses.includes(e.status)));
      }
    }
    const lastPaidEmis = allPaidEmis
      .sort((a, b) => new Date(b.emiRepaidDate || 0) - new Date(a.emiRepaidDate || 0))
      .slice(0, 3);

    const dummyPayments = lastPaidEmis.map(emi => {
      const isOverduePaid = emi.status === 4;
      const badge = 'PAID';
      const type = isOverduePaid ? 'overdue-paid' : 'paid';
      return {
        name: emi.paymentModeLabel || 'EMI Payment',
        date: `Paid on ${fmtEmiDate(emi.emiRepaidDate)}`,
        amount: `₹${fmt(emi.repaidEmiAmount || 0)}`,
        badge: badge,
        type: type,
        isOverduePaid: isOverduePaid
      };
    });

    const heroEmis = firstLoan ? (emiData[firstLoan.loanId] || []) : [];
    const heroPaid = heroEmis.filter(e => e.status == 2 || e.status == 4 || e.status == 9).length;
    const heroTotal = heroEmis.length;
    const heroPercent = heroTotal ? Math.round((heroPaid / heroTotal) * 100) : 0;
    const heroRemaining = heroTotal - heroPaid;
    const heroR = 44, heroCirc = 2 * Math.PI * heroR;
    const heroDash = (heroPercent / 100) * heroCirc;
    const heroRoi = firstLoan?.roi || firstLoan?.interestRate;

    return (
      <section className="db-dash-section">

        {/* ══ NEW UNIFIED LOAN CARD LAYOUT ══ */}
        {loading && <p className="db-loading" style={{ padding: '40px 0' }}>Loading…</p>}
        {!loading && loans.length === 0 && closedLoans.length === 0 && (
          <div className="db-active-empty" style={{ padding: '40px 0' }}>
            <p className="db-active-empty-title">{appDetails.length > 0 ? 'Application in Progress' : 'No Loans Yet'}</p>
            <p className="db-active-empty-sub">
              {appDetails.length > 0
                ? 'Your loan application is being reviewed.'
                : "You don't have any loans yet."}
            </p>
          </div>
        )}
        {!loading && (loans.length > 0 || closedLoans.length > 0) && (
          <div className="db-luc-wrapper">
            {loans.length === 0 && closedLoans.length > 0 && (
              <div className="db-apply-banner">
                <div className="db-apply-banner-text">
                  <p className="db-apply-banner-title">Looking for a new loan?</p>
                  <p className="db-apply-banner-sub">Your previous loan is fully closed. Apply for a new loan and continue your journey with Feemonk.</p>
                </div>
                <button className="db-apply-banner-btn" onClick={handleApplyNewLoan}>Apply Now →</button>
              </div>
            )}
            {(loans.length > 0 ? loans : closedLoans).map((loan, idx) => {
              const loanEmis = emiData[loan.loanId] || [];
              const lPaid = loanEmis.filter(e => e.status == 2 || e.status == 4 || e.status == 8 || e.status == 9).length;
              const lTotal = loanEmis.length;
              const lPercent = lTotal ? Math.round((lPaid / lTotal) * 100) : 0;
              const lRemaining = lTotal - lPaid;
              const R = 44, circ = 2 * Math.PI * R;
              const dash = (lPercent / 100) * circ;
              const nextDueEmi = loanEmis.find(e => e.status == 1 || e.status == 5 || e.status == 3);
              const parseEmiDate = (d) => { if (!d) return 0; const p = d.split('/'); return p.length === 3 ? new Date(p[2], p[1] - 1, p[0]).getTime() : 0; };
              const lastPaidEmi = [...loanEmis]
                .filter(e => !e.advanceEmi && (e.status == 2 || e.status == 4 || e.status == 8 || e.status == 9) && e.emiRepaidDate)
                .sort((a, b) => parseEmiDate(b.emiRepaidDate) - parseEmiDate(a.emiRepaidDate))[0];
              const nextEmiDate = nextDueEmi?.dueDate ? fmtEmiDate(nextDueEmi.dueDate) : '--';
              const profile = loanProfiles[loan.loanId];
              const isClosed = loans.length === 0;
              const isDocsOpen = docsExpanded[loan.loanId];
              const loanMandate = loanEmis.find(e => e.status == 7);
              const loanOverdue = loanEmis.find(e => e.status == 5);
              const loanUpcoming = loanEmis.find(e => e.status == 1);
              const repayType = loanOverdue ? 'Overdue Payment' : loanUpcoming ? 'Scheduled Repayment' : 'Active';
              const loanAlertEmi = loanOverdue || loanUpcoming;

              return (
                <div key={idx} className="db-loan-unified-card">

                  {/* ── LEFT ── */}
                  <div className="db-luc-left">
                    <div className="db-luc-badge-row">
                      <span className={`db-luc-status-badge${isClosed ? ' closed' : ''}`}>
                        {isClosed ? 'CLOSED' : 'ACTIVE'}
                      </span>
                      <span className="db-luc-repay-type">{isClosed ? 'Loan Closed' : repayType}</span>
                    </div>
                    <p className="db-luc-loan-id">{loan.legacyLoanId || loan.loanId}</p>
                    <p className="db-luc-loan-type"></p>
                    <div className="db-luc-meta-row">
                      {!isClosed ? (
                        <>
                          <div>
                            <p className="db-luc-meta-label">DUE DATE</p>
                            <p className="db-luc-meta-val">{nextEmiDate}</p>
                          </div>
                          <div>
                            <p className="db-luc-meta-label">EMI AMOUNT</p>
                            <p className="db-luc-meta-val db-luc-meta-red">₹{fmt(loan.emi)}</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <p className="db-luc-meta-label">LOAN AMOUNT</p>
                            <p className="db-luc-meta-val">₹{fmt(loan.pricipalAmount)}</p>
                          </div>
                          <div>
                            <p className="db-luc-meta-label">CLOSURE DATE</p>
                            <p className="db-luc-meta-val">{loan.endDate || loan.lastEmiDate || loan.startDate || '--'}</p>
                          </div>
                        </>
                      )}
                    </div>
                    <div style={{ flex: 1 }} />
                    {!isClosed && (loanMandate || loanAlertEmi) && (
                      <div className="db-luc-alert-text">
                        {loanMandate
                          ? <>🏦 Your mandate has been presented to the bank. Payments are not allowed until the mandate process is complete.</>
                          : loanOverdue
                          ? <>⚠️ Your EMI of <strong>₹{fmt(loanOverdue.remainingAmountToBePaid || loan.emi)}</strong>{loanOverdue.dueDate ? <> due on <strong>{fmtEmiDate(loanOverdue.dueDate)}</strong></> : ''} is overdue.</>
                          : <>🔔 Next installment of <strong>₹{fmt(loanUpcoming.emiAmount)}</strong> is due{loanUpcoming.dueDate ? <> on <strong>{fmtEmiDate(loanUpcoming.dueDate)}</strong></> : ' soon'}.</>}
                      </div>
                    )}
                    <div className="db-luc-btn-row">
                      {!isClosed && !loanMandate && loanAlertEmi && (
                        <button className="db-luc-pay-btn" onClick={() => handleAlertPay(loan, loanAlertEmi, () => setMandateModal(true))}>
                          <span className="pay-btn-full">Pay EMI</span>
                          <span className="pay-btn-short">Pay Now</span>
                        </button>
                      )}
                      {!isClosed && (
                        <button className="db-luc-preclose-mid-btn" onClick={() => handleForecloseButtonClick(loan)}>
                          <span className="preclose-mid-full">Pre-close</span>
                          <span className="preclose-mid-short">Close</span>
                        </button>
                      )}
                      <button className="db-luc-stmt-btn" onClick={() => handleStatementDownload(loan, loanEmis)}>
                        <svg className="db-luc-stmt-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', marginRight: 6 }}>
                          <path d="M12 16l-6-6h4V4h4v6h4l-6 6z" fill="currentColor"/>
                          <path d="M4 20h16v-2H4v2z" fill="currentColor"/>
                        </svg>
                        <span className="stmt-full">Statement</span>
                        <span className="stmt-short">Stmt</span>
                      </button>
                    </div>
                  </div>

                  {/* ── MIDDLE ── */}
                  <div className="db-luc-mid">
                    <div className="db-hero-insight-badge">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="white"/>
                      </svg>
                      Quick Overview
                    </div>
                    <div className="db-hero-stats-row" style={{ marginTop: 16 }}>
                      <div className="db-hero-donut-wrap">
                        {(() => {
                          const progColor = lPercent >= 67 ? '#22c55e' : lPercent >= 34 ? '#f97316' : '#D32028';
                          const trackColor = lPercent >= 67 ? '#dcfce7' : lPercent >= 34 ? '#ffedd5' : '#f0d0d0';
                          return (
                            <svg width="90" height="90" viewBox="8 8 104 104">
                              <circle cx="60" cy="60" r={R} fill="none" stroke={trackColor} strokeWidth="10"/>
                              <circle cx="60" cy="60" r={R} fill="none" stroke={progColor} strokeWidth="10"
                                strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
                                transform="rotate(-90 60 60)"/>
                              <text x="60" y="56" textAnchor="middle" fontSize="18" fontWeight="700" fill="#1a1a1a">{lPercent}%</text>
                              <text x="60" y="72" textAnchor="middle" fontSize="10" fill="#888">REPAID</text>
                            </svg>
                          );
                        })()}
                      </div>
                      <div className="db-hero-stats-info">
                        <div className="db-hero-tenure-top-row">
                          <p className="db-hero-tenure-label">Repayment</p>
                          <p className="db-hero-tenure-val" style={{ color: lPercent >= 67 ? '#22c55e' : lPercent >= 34 ? '#f97316' : '#D32028' }}>{lRemaining} of {lTotal} EMIs remaining</p>
                        </div>
                        <div className="db-hero-tenure-bar">
                          <div className="db-hero-tenure-fill" style={{ width: `${lPercent}%`, background: lPercent >= 67 ? '#22c55e' : lPercent >= 34 ? '#f97316' : '#D32028' }} />
                        </div>
                      </div>
                    </div>
                    {(() => {
                      const allEmis = emiData[loan.loanId] || [];
                      if (allEmis.length === 0) return null;
                      const dotColor = (e) => {
                        if (e.advanceEmi) return '#2E90FA';
                        const n = Number(e.status);
                        if ([2, 4, 8, 9, 11].includes(n)) return '#12B76A';
                        if ([3, 6].includes(n)) return '#F8A31F';
                        if ([5, 12].includes(n)) return '#D22129';
                        if (n === 1) return '#E4E7EC';
                        return '#D0D5DD';
                      };
                      const dotTitle = { 1: 'Upcoming', 2: 'Paid', 3: 'Partly Paid', 4: 'Paid (Late)', 5: 'Overdue', 6: 'Paused', 7: 'Mandate In Progress', 8: 'Paid', 9: 'Paid', 10: 'Refunded', 11: 'Settled', 12: 'Written Off', 13: 'Opted Out' };
                      return (
                        <div className="db-emi-track">
                          <p className="db-emi-track-label">
                            <span className="db-emi-track-label-desktop">Payment Track</span>
                            <span className="db-emi-track-label-mobile" style={{ color: lPercent >= 67 ? '#22c55e' : lPercent >= 34 ? '#f97316' : '#D32028' }}>{lRemaining} of {lTotal} EMIs remaining</span>
                          </p>
                          <div className="db-emi-track-dots">
                            {allEmis.map((e, i) => (
                              <span key={i} className="db-emi-track-dot" style={{ background: dotColor(e) }} title={`EMI ${i + 1} — ${e.advanceEmi ? 'Advance' : (dotTitle[e.status] || e.status)}`} />
                            ))}
                          </div>
                          <div className="db-emi-track-legend">
                            <span className="db-emi-track-legend-item"><span className="db-emi-track-dot-sm" style={{ background: '#12B76A' }} />Paid</span>
                            <span className="db-emi-track-legend-item"><span className="db-emi-track-dot-sm" style={{ background: '#2E90FA' }} />Advance</span>
                            <span className="db-emi-track-legend-item"><span className="db-emi-track-dot-sm" style={{ background: '#F8A31F' }} />Partial</span>
                            <span className="db-emi-track-legend-item"><span className="db-emi-track-dot-sm" style={{ background: '#D22129' }} />Overdue</span>
                            <span className="db-emi-track-legend-item"><span className="db-emi-track-dot-sm" style={{ background: '#E4E7EC' }} />Upcoming</span>
                          </div>
                        </div>
                      );
                    })()}
                    {!isClosed && (
                      <div className="db-hero-preclose-bar" style={{ marginTop: 'auto' }}>
                        <div className="db-hero-preclose-top-row">
                          <div className="db-hero-preclose-icon">⚡</div>
                          <p className="db-hero-preclose-text">Pre-close this loan to apply for new loans</p>
                        </div>
                        <button className="db-hero-preclose-btn" onClick={() => handleForecloseButtonClick(loan)}>
                          <span className="preclose-btn-full">Pre-close Now</span>
                          <span className="preclose-btn-short">Pre-close</span>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* ── RIGHT ── */}
                  <div className="db-luc-right">
                     <div className="db-luc-section">
                      <p className="db-luc-section-title"></p>
                      <div className="db-luc-asset-list">
                        <div className="db-luc-asset-item">
                          <div className="db-luc-asset-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <rect x="2" y="7" width="20" height="14" rx="2" stroke="#888" strokeWidth="1.6"/>
                              <path d="M2 10h20" stroke="#888" strokeWidth="1.4"/>
                              <path d="M6 15h3M6 17.5h2" stroke="#888" strokeWidth="1.2" strokeLinecap="round"/>
                            </svg>
                          </div>
                          <div>
                            <p className="db-luc-asset-name">Repayment Bank</p>
                            <p className="db-luc-asset-sub">
                              {profile?.mandateDetails?.accountNumber
                                ? `••••••${String(profile.mandateDetails.accountNumber).slice(-4)}`
                                : '—'}
                            </p>
                          </div>
                        </div>
                        <div className="db-luc-docs-wrap">
                          <div className="db-luc-asset-item db-luc-asset-item--btn"
                            onClick={() => setDocsExpanded(prev => ({ ...prev, [loan.loanId]: !prev[loan.loanId] }))}>
                            <div className="db-luc-asset-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#888" strokeWidth="1.6" strokeLinejoin="round"/>
                                <polyline points="14 2 14 8 20 8" stroke="#888" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <p className="db-luc-asset-name">Download Documents</p>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                              style={{ marginLeft: 'auto', transform: isDocsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                              <polyline points="6 9 12 15 18 9" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          {isDocsOpen && (
                            <div className="db-luc-docs-dropdown">
                              <div className="db-luc-doc-item">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#D32028" strokeWidth="1.6" strokeLinejoin="round"/>
                                  <polyline points="14 2 14 8 20 8" stroke="#D32028" strokeWidth="1.6"/>
                                </svg>
                                <span>Sanction Letter</span>
                                {profile?.sanctionLetterUrl ? (
                                  <button onClick={() => handleDocumentDownload(profile.sanctionLetterUrl, 'SanctionLetter.pdf')} style={{marginTop:"-0.1px"}}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                      <path d="M12 16l-6-6h4V4h4v6h4l-6 6z" fill="currentColor"/>
                                      <path d="M4 20h16v-2H4v2z" fill="currentColor"/>
                                    </svg>
                                  </button>
                                ) : <span className="db-docs-generating" style={{ marginLeft: 'auto' }}>—</span>}
                              </div>
                              <div className="db-luc-doc-item">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#D32028" strokeWidth="1.6" strokeLinejoin="round"/>
                                  <polyline points="14 2 14 8 20 8" stroke="#D32028" strokeWidth="1.6"/>
                                  <line x1="8" y1="13" x2="16" y2="13" stroke="#D32028" strokeWidth="1.2" strokeLinecap="round"/>
                                  <line x1="8" y1="16" x2="16" y2="16" stroke="#D32028" strokeWidth="1.2" strokeLinecap="round"/>
                                </svg>
                                <span>Signed Agreement</span>
                                {profile?.agreementUrl ? (
                                  <button onClick={() => handleDocumentDownload(profile.agreementUrl, 'SignedAgreement.pdf')} style={{marginTop:"-0.1px"}}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                      <path d="M12 16l-6-6h4V4h4v6h4l-6 6z" fill="currentColor"/>
                                      <path d="M4 20h16v-2H4v2z" fill="currentColor"/>
                                    </svg>
                                  </button>
                                ) : <span className="db-docs-generating" style={{ marginLeft: 'auto' }}>—</span>}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="db-luc-divider" />
                   
                    <div className="db-luc-section">
                      <div className="db-luc-section-head">
                        <p className="db-luc-section-title">RECENT ACTIVITY</p>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="9" stroke="#bbb" strokeWidth="1.8"/>
                          <polyline points="12 6 12 12 16 14" stroke="#bbb" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <div className="db-luc-activity-list">
                        {lastPaidEmi && (
                          <div className="db-luc-activity-item">
                            <div className="db-luc-act-icon paid">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="9" stroke="#12B76A" strokeWidth="2"/>
                                <polyline points="8 12 11 15 16 9" stroke="#12B76A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <div className="db-luc-act-info">
                              <p className="db-luc-act-name">EMI Paid</p>
                              <p className="db-luc-act-date">₹{fmt(lastPaidEmi.emiAmount)} • {fmtEmiDate(lastPaidEmi.emiRepaidDate)}</p>
                            </div>
                            <span className="db-luc-act-badge paid">PAID</span>
                          </div>
                        )}
                        {nextDueEmi && (
                          <div className="db-luc-activity-item">
                            <div className={`db-luc-act-icon ${nextDueEmi.status == 5 ? 'overdue' : 'due'}`}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="9" stroke={nextDueEmi.status == 5 ? '#D32028' : '#F8A31F'} strokeWidth="2"/>
                                <line x1="12" y1="8" x2="12" y2="13" stroke={nextDueEmi.status == 5 ? '#D32028' : '#F8A31F'} strokeWidth="2" strokeLinecap="round"/>
                                <circle cx="12" cy="16" r="1" fill={nextDueEmi.status == 5 ? '#D32028' : '#F8A31F'}/>
                              </svg>
                            </div>
                            <div className="db-luc-act-info">
                              <p className="db-luc-act-name">Next Due</p>
                              <p className="db-luc-act-date">₹{fmt(nextDueEmi.remainingAmountToBePaid || nextDueEmi.emiAmount)} • {fmtEmiDate(nextDueEmi.dueDate)}</p>
                            </div>
                            <span className={`db-luc-act-badge ${nextDueEmi.status == 5 ? 'overdue' : 'due'}`}>
                              {nextDueEmi.status == 5 ? 'OVERDUE' : 'DUE'}
                            </span>
                          </div>
                        )}
                        {!lastPaidEmi && !nextDueEmi && (
                          <p style={{ fontSize: 12, color: '#aaa', margin: '8px 0' }}>No recent activity</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* ── Bottom Row: Contact Support + Financial Wisdom ── */}
            <div className="db-bottom-row">
              <div className="db-assist-banner">
                <div className="db-assist-header">
                  <div className="db-assist-icon-wrap">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.88 9.77a19.79 19.79 0 01-3.07-8.68A2 2 0 012.78 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.75a16 16 0 006.29 6.29l1.1-1.1a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="db-assist-text">
                    <p className="db-assist-title">Contact Support</p>
                    <p className="db-assist-sub">Our team is available Mon-Sat, 10:00 AM to 6:00 PM</p>
                  </div>
                </div>
                <div className="db-assist-contacts">
                  <div className="db-assist-contact">
                    <span className="db-assist-contact-label">CALL US</span>
                    <p className="db-assist-contact-val">+91 95131 68846</p>
                  </div>
                  <div className="db-assist-contact">
                    <span className="db-assist-contact-label">EMAIL</span>
                    <p className="db-assist-contact-val db-assist-contact-email">hello@feemonk.com</p>
                  </div>
                </div>
              </div>

              <div className="db-wisdom-quote">
                <div className="db-wisdom-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="db-wisdom-body">
                  <p className="db-wisdom-text">"{wisdomQuote.text}" <span className="db-wisdom-attr">— {wisdomQuote.attr}</span></p>
                </div>
                <div className="db-wisdom-deco" aria-hidden="true">❝</div>
              </div>
            </div>
          </div>
        )}
        {/* ══ OLD HERO — kept hidden, referenced by old code ══ */}
        <div className={`db-dash-hero${isOverdue ? ' db-dash-hero--overdue' : ''}`} style={{ display: 'none' }}>
          <div className="db-dash-hero-img-mob">
            {firstLoan && (
              <div className="db-hero-mob-widget">
                <div className="db-hero-stats-row">
                  <div className="db-hero-donut-wrap">
                    <svg width="90" height="90" viewBox="0 0 120 120">
                      <circle cx="60" cy="60" r={heroR} fill="none" stroke="#f0d0d0" strokeWidth="10"/>
                      <circle cx="60" cy="60" r={heroR} fill="none" stroke="#D32028" strokeWidth="10"
                        strokeDasharray={`${heroDash} ${heroCirc}`} strokeLinecap="round"
                        transform="rotate(-90 60 60)"/>
                      <text x="60" y="56" textAnchor="middle" fontSize="18" fontWeight="700" fill="#1a1a1a">{heroPercent}%</text>
                      <text x="60" y="72" textAnchor="middle" fontSize="10" fill="#888">REPAID</text>
                    </svg>
                  </div>
                  <div className="db-hero-stats-info">
                    <p className="db-hero-tenure-label">Repayment</p>
                    <p className="db-hero-tenure-val" style={{ color: heroPercent >= 67 ? '#22c55e' : heroPercent >= 34 ? '#f97316' : '#D32028' }}>{heroRemaining} of {heroTotal} EMIs remaining</p>
                    <div className="db-hero-tenure-bar">
                      <div className="db-hero-tenure-fill" style={{ width: `${heroPercent}%`, background: heroPercent >= 67 ? '#22c55e' : heroPercent >= 34 ? '#f97316' : '#D32028' }} />
                    </div>
                  </div>
                </div>
                <div className="db-hero-preclose-bar">
                  <div className="db-hero-preclose-top-row">
                    <div className="db-hero-preclose-icon">⚡</div>
                    <p className="db-hero-preclose-text">Pre-close this loan to apply for new loans</p>
                  </div>
                  <button className="db-hero-preclose-btn" onClick={() => handleForecloseButtonClick(firstLoan)}>
                    <span className="preclose-btn-full">Pre-close Now</span>
                    <span className="preclose-btn-short">Pre-close</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="db-dash-hero-inner">
            <div className="db-dash-hero-left">
              <div className={`db-dash-badge${isOverdue ? ' db-dash-badge--overdue' : ''}`}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.8"/>
                  <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
                {badge}
              </div>
              <h2 className="db-dash-title-lg">{titleLg}</h2>
              <h2 className="db-dash-title-sm">{titleSm}</h2>
              <p className="db-dash-desc">{desc}</p>
              <div className="db-dash-actions">
                {firstLoan ? (
                  <>
                    <button className="db-dash-pay-btn" onClick={() => {
                      if (!emiAlert) return;
                      handleAlertPay(emiAlert.loan, emiAlert.emi, () => setMandateModal(true));
                    }}>
                      Pay EMI →
                    </button>
                    <button className="db-dash-detail-btn" onClick={() => setSearchParams({ tab: 'active' })}>
                      View Details
                    </button>
                  </>
                ) : !loading && closedLoans.length > 0 ? (
                  <>
                    {appDetails.every(a => (a.status || '').toLowerCase() === 'disbursed') && (
                      <button className="db-dash-apply-btn" onClick={() => navigate('/eligibility/pan')}>
                        Apply for New Loan
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '8px', verticalAlign: 'middle' }}>
                          <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    )}
                  </>
                ) : !loading && appDetails.length > 0 ? (
                  <button className="db-dash-pay-btn" onClick={() => setSearchParams({ tab: 'applications' })}>
                    View Application →
                  </button>
                ) : !loading ? (
                  <button className="db-dash-pay-btn" onClick={() => navigate('/eligibility/pan')}>
                    Apply Now →
                  </button>
                ) : null}
              </div>
            </div>

            {firstLoan && (
              <div className="db-dash-hero-right">
                <div className="db-hero-insight-badge">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="white"/>
                  </svg>
                  Quick Overview
                </div>
                <div className="db-hero-stats-row">
                  <div className="db-hero-donut-wrap">
                    <svg width="120" height="120" viewBox="0 0 120 120">
                      <circle cx="60" cy="60" r={heroR} fill="none" stroke="#f0d0d0" strokeWidth="10"/>
                      <circle cx="60" cy="60" r={heroR} fill="none" stroke="#D32028" strokeWidth="10"
                        strokeDasharray={`${heroDash} ${heroCirc}`} strokeLinecap="round"
                        transform="rotate(-90 60 60)"/>
                      <text x="60" y="56" textAnchor="middle" fontSize="18" fontWeight="700" fill="#1a1a1a">{heroPercent}%</text>
                      <text x="60" y="72" textAnchor="middle" fontSize="10" fill="#888">REPAID</text>
                    </svg>
                  </div>
                  <div className="db-hero-stats-info">
                    <div>
                      <p className="db-hero-tenure-label">Repayment</p>
                      <p className="db-hero-tenure-val" style={{ color: heroPercent >= 67 ? '#22c55e' : heroPercent >= 34 ? '#f97316' : '#D32028' }}>{heroRemaining} of {heroTotal} EMIs remaining</p>
                      <div className="db-hero-tenure-bar">
                        <div className="db-hero-tenure-fill" style={{ width: `${heroPercent}%`, background: heroPercent >= 67 ? '#22c55e' : heroPercent >= 34 ? '#f97316' : '#D32028' }} />
                      </div>
                    </div>
                    <div className="db-hero-meta-row">
                      {heroRoi ? (
                        <div className="db-hero-meta-item">
                          <p className="db-hero-meta-label">Interest Rate</p>
                          <p className="db-hero-meta-val">{heroRoi}% p.a.</p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="db-hero-preclose-bar">
                  <div className="db-hero-preclose-top-row">
                    <div className="db-hero-preclose-icon">⚡</div>
                    <p className="db-hero-preclose-text">Pre-close this loan to apply for new loans</p>
                  </div>
                  <button className="db-hero-preclose-btn" onClick={() => handleForecloseButtonClick(firstLoan)}>
                    <span className="preclose-btn-full">Pre-close Now</span>
                    <span className="preclose-btn-short">Pre-close</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Loan summary + side cards — HIDDEN (replaced by unified card above) ── */}
        {false && !loading && (loans.length > 0 || closedLoans.length > 0) && (
          <div className="db-dash-bottom-row">
                {loans.length > 0 ? loans.map((loan, idx) => {
                  const loanEmis = emiData[loan.loanId] || [];
                  const nextEmi = loanEmis.find(e => e.status == 1 || e.status == 3 || e.status == 5);
                  const nextEmiDate = nextEmi?.dueDate ? fmtEmiDate(nextEmi.dueDate) : '--';
                  const profile = loanProfiles[loan.loanId];
                  return (
                  <React.Fragment key={idx}>
                    {/* Loan Details Card */}
                    <div className="db-simple-loan-card">
                      <div className="db-slc-col-item">
                        <p className="db-slc-label">LOAN ACCOUNT ID</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                          <p className="db-slc-id">{loan.legacyLoanId || loan.loanId}</p>
                          <span className="db-slc-active-badge">ACTIVE</span>
                        </div>
                      </div>
                      <div className="db-slc-col-item">
                        <p className="db-slc-label">NEXT EMI DUE DATE</p>
                        <p className="db-slc-due-date">{nextEmiDate}</p>
                      </div>
                      <div className="db-slc-col-item">
                        <p className="db-slc-label">EMI AMOUNT</p>
                        <p className="db-slc-emi">₹{fmt(loan.emi)}</p>
                      </div>
                      <button className="db-slc-statement-btn db-slc-full-btn" onClick={() => handleStatementDownload(loan, emiData[loan.loanId] || [])} style={{marginTop:'-0.1px'}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                          <path d="M12 16l-6-6h4V4h4v6h4l-6 6z" fill="currentColor"/>
                          <path d="M4 20h16v-2H4v2z" fill="currentColor"/>
                        </svg>
                        Loan Statement
                      </button>
                      {/* <button className="db-slc-receipts-btn db-slc-full-btn" onClick={() => goToReceipts(loan)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          <line x1="9" y1="13" x2="15" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                          <line x1="9" y1="17" x2="15" y2="17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                        Receipts
                      </button> */}
                    </div>

                    {/* Documents Card */}
                    <div className="db-docs-card">
                      <div className="db-docs-header">
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                          <h3 className="db-docs-title">Documents</h3>
                          <span className="db-docs-loan-id">{loan.legacyLoanId || loan.loanId}</span>
                        </div>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" stroke="#888" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className="db-docs-list">
                        <div className="db-docs-item">
                          <div className="db-docs-icon db-docs-icon--red">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="#D32028"/>
                              <polyline points="14 2 14 8 20 8" fill="#B01B21"/>
                            </svg>
                          </div>
                          <span className="db-docs-name">Sanction Letter</span>
                          {profile?.sanctionLetterUrl ? (
                            <button className="db-docs-dl-btn" onClick={() => handleDocumentDownload(profile.sanctionLetterUrl, 'SanctionLetter.pdf')}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M12 16l-6-6h4V4h4v6h4l-6 6z" fill="currentColor"/>
                                <path d="M4 20h16v-2H4v2z" fill="currentColor"/>
                              </svg>
                            </button>
                          ) : (
                            <span className="db-docs-generating">—</span>
                          )}
                        </div>
                        <div className="db-docs-item">
                          <div className="db-docs-icon db-docs-icon--red">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="#D32028"/>
                              <polyline points="14 2 14 8 20 8" fill="#B01B21"/>
                              <line x1="8" y1="13" x2="16" y2="13" stroke="#fff" strokeWidth="1.5"/>
                              <line x1="8" y1="16" x2="16" y2="16" stroke="#fff" strokeWidth="1.5"/>
                            </svg>
                          </div>
                          <span className="db-docs-name">Signed Agreement</span>
                          {profile?.agreementUrl ? (
                            <button className="db-docs-dl-btn" onClick={() => handleDocumentDownload(profile.agreementUrl, 'SignedAgreement.pdf')}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M12 16l-6-6h4V4h4v6h4l-6 6z" fill="currentColor"/>
                                <path d="M4 20h16v-2H4v2z" fill="currentColor"/>
                              </svg>
                            </button>
                          ) : (
                            <span className="db-docs-generating">—</span>
                          )}
                        </div>
                        <div className="db-docs-item">
                          <div className="db-docs-icon db-docs-icon--grey">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <rect x="2" y="7" width="20" height="14" rx="2" fill="#888"/>
                              <path d="M2 10h20" stroke="#fff" strokeWidth="1.5"/>
                              <path d="M6 15h3M6 17.5h2" stroke="#fff" strokeWidth="1.2" strokeLinecap="round"/>
                            </svg>
                          </div>
                          <div className="db-docs-bank-info">
                            <span className="db-docs-name">Repayment Bank Account</span>
                            <span className="db-docs-account">{profile?.mandateDetails?.accountNumber || '—'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                  );
                }) : closedLoans.map((loan, idx) => (
                  <div key={idx} className="db-simple-loan-card">
                    <div className="db-slc-top">
                      <div>
                        <p className="db-slc-label">LOAN ACCOUNT ID</p>
                        <p className="db-slc-id">{loan.legacyLoanId || loan.loanId}</p>
                      </div>
                      <span className="db-slc-closed-badge">CLOSED</span>
                    </div>
                    <div className="db-slc-meta">
                      <div>
                        <p className="db-slc-label">LOAN AMOUNT</p>
                        <p className="db-slc-emi">₹{fmt(loan.pricipalAmount)}</p>
                      </div>
                      <div>
                        <p className="db-slc-label">CLOSURE DATE</p>
                        <p className="db-slc-emi" style={{ fontSize: 14 }}>{loan.endDate || loan.startDate || '--'}</p>
                      </div>
                    </div>
                    <div className="db-slc-actions">
                      <button className="db-slc-statement-btn" onClick={() => handleStatementDownload(loan, emiData[loan.loanId] || [])}>
                        Loan Statement
                      </button>
                      <button className="db-slc-receipts-btn" onClick={() => goToReceipts(loan)}>
                        Receipts
                      </button>
                    </div>
                  </div>
                ))}

            {/* Payment History & Timeline */}
            <div className="db-payment-history-card">
                <h3 className="db-ph-title">Payment History &amp; Timeline</h3>
                <div className="db-ph-list">
                  {dummyPayments.length === 0 && (
                    <div className="db-ph-empty">
                      <div className="db-ph-empty-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                          <rect x="2" y="5" width="20" height="16" rx="2" stroke="#D0D5DD" strokeWidth="1.5"/>
                          <path d="M2 10h20" stroke="#D0D5DD" strokeWidth="1.5"/>
                          <path d="M6 15h4M6 18h3" stroke="#D0D5DD" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <p className="db-ph-empty-title">No payments yet</p>
                      <p className="db-ph-empty-sub">Your payment history will appear here once your first EMI is processed.</p>
                    </div>
                  )}
                  {dummyPayments.map((p, i) => (
                    <div key={i} className={`db-ph-row db-ph-row--${p.type}`}>
                      <div className={`db-ph-icon-wrap db-ph-icon-wrap--${p.type}`}>
                        {p.isOverduePaid ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="9" stroke="#D32028" strokeWidth="2"/>
                            <polyline points="8 12 11 15 16 9" stroke="#D32028" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : p.type === 'paid' ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="9" stroke="#12B76A" strokeWidth="2"/>
                            <polyline points="8 12 11 15 16 9" stroke="#12B76A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="9" stroke="#F04438" strokeWidth="2"/>
                            <line x1="12" y1="8" x2="12" y2="13" stroke="#F04438" strokeWidth="2" strokeLinecap="round"/>
                            <circle cx="12" cy="16.5" r="1.2" fill="#F04438"/>
                          </svg>
                        )}
                      </div>
                      <div className="db-ph-info">
                        <p className="db-ph-name">EMI Payment</p>
                        <p className={`db-ph-date${p.isOverduePaid ? ' db-ph-date--overdue' : ''}`}>{p.date}</p>
                      </div>
                      <div className="db-ph-right">
                        <p className={`db-ph-amount${p.isOverduePaid ? ' db-ph-amount--overdue' : ''}`}>{p.amount}</p>
                        <span className={`db-ph-badge db-ph-badge--${p.isOverduePaid ? 'dropped' : 'disbursed'}`}>{p.badge}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            <div className="db-payment-help-card">
                <h4 className="db-phc-title">Payment Help</h4>
                <p className="db-phc-body">Struggling with this month's payment? Speak with our team to explore flexible options.</p>
                <div className="db-phc-contacts">
                  <div className="db-phc-contact-item">
                    <span className="db-phc-contact-label">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', marginRight: 5 }}>
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.88 9.77a19.79 19.79 0 01-3.07-8.68A2 2 0 012.78 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.75a16 16 0 006.29 6.29l1.1-1.1a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="rgba(255,255,255,0.6)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Call us
                    </span>
                    <p className="db-phc-contact-val">+91 95131 68846</p>
                  </div>
                  <div className="db-phc-contact-item">
                    <span className="db-phc-contact-label">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', marginRight: 5 }}>
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="rgba(255,255,255,0.6)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="22,6 12,13 2,6" stroke="rgba(255,255,255,0.6)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Email us
                    </span>
                    <p className="db-phc-contact-val db-phc-contact-val--email">hello@feemonk.com</p>
                  </div>
                </div>
              </div>

              {/* <div className="db-avoid-penalties-card">
                <h4 className="db-apc-title">Avoid Penalties</h4>
                <ul className="db-apc-list">
                  <li><span className="db-apc-x">✕</span> Bounce charge</li>
                  <li><span className="db-apc-x">✕</span> Daily late fee</li>
                </ul>
              </div> */}
          </div>
        )}
      </section>
    );
  };

  /* ── render ── */
  return (
    <div className="db-root">

      {/* Mandate active — payment blocked modal */}
      <Modal isOpen={mandateModal} toggle={() => setMandateModal(false)} centered className="mandate-modal" backdrop={true}>
        <ModalBody className="mandate-modal-body">
          <div className="mandate-modal-icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="#FFF3E0"/>
              <path d="M12 7v5" stroke="#F8A31F" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="12" cy="16" r="1.2" fill="#F8A31F"/>
            </svg>
          </div>
          <h3 className="mandate-modal-title">Mandate Presented</h3>
          <p className="mandate-modal-msg">Your mandate has been presented to the bank. Payments are not allowed until the mandate process is complete.</p>
          <button className="mandate-modal-btn" onClick={() => setMandateModal(false)}>Got it</button>
        </ModalBody>
      </Modal>

      <Modal isOpen={openForeClose} toggle={handleForeClose} centered className="fc-modal" backdrop="static" keyboard={false}>
        <ModalBody className="fc-modal-body">
          {foreclosureDetails.amount !== null ? (
            <>
              {/* Header */}
              <div className="fc-header">
                <div className="fc-icon-wrap">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#D32028"/>
                  </svg>
                </div>
                <h2 className="fc-title">Foreclose Loan</h2>
                <p className="fc-subtitle">Review the breakdown before proceeding with full loan closure.</p>
                <button className="fc-close-btn" onClick={handleForeClose}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              {/* Breakdown */}
              <div className="fc-breakdown">
                <div className="fc-row">
                  <span className="fc-row-label">Principal Outstanding</span>
                  <span className="fc-row-val">₹ {fmt(foreclosureDetails.amount)}</span>
                </div>
                <div className="fc-row">
                  <span className="fc-row-label">Foreclosure Charges</span>
                  <span className="fc-row-val">
                    {foreclosureDetails.isWaived ? (
                      <>
                        <span className="fc-strikethrough">₹ {fmt(foreclosureDetails.charges)}</span>
                        <span className="fc-waived-badge">Waived</span>
                      </>
                    ) : (
                      `₹ ${fmt(foreclosureDetails.charges)}`
                    )}
                  </span>
                </div>
              </div>

              {/* Net Payable */}
              <div className="fc-net-row">
                <span className="fc-net-label">Net Payable</span>
                <span className="fc-net-val">₹ {fmt(foreclosureDetails.netAmount)}</span>
              </div>

              {/* Info note */}
              <p className="fc-note">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{marginRight:5,flexShrink:0,marginTop:1}}>
                  <circle cx="12" cy="12" r="10" stroke="#F8A31F" strokeWidth="2"/>
                  <path d="M12 8v4m0 4h.01" stroke="#F8A31F" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Paying this amount will foreclose your loan and relieve you of all future EMIs of this loan.
              </p>

              {/* Actions */}
              <div className="fc-actions">
                <button className="fc-cancel-btn" onClick={handleForeClose}>Cancel</button>
                <button
                  className="fc-pay-btn"
                  onClick={() =>
                    razorpayCheckoutHandler(
                      [],
                      foreclosureDetails.netAmount,
                      sessionStorage.getItem("PreCloseLoanId"),
                      token,
                      1
                    )
                  }
                >
                  Pay ₹ {fmt(foreclosureDetails.netAmount)}
                </button>
              </div>
            </>
          ) : (
            <div className="fc-loading">
              <div className="fc-spinner" />
              <p>Fetching details…</p>
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* ══ RECEIPTS MODAL ══ */}
      <Modal isOpen={!!receiptsModal} toggle={() => setReceiptsModal(null)} backdrop="static" centered className="db-receipts-modal">
        <ModalHeader toggle={() => setReceiptsModal(null)} className="db-receipts-modal-header">
          Receipts — {receiptsModal?.legacyLoanId || receiptsModal?.loanId}
        </ModalHeader>
        <ModalBody className="db-receipts-modal-body">
          {receiptsLoading ? (
            <div className="db-receipts-loading">
              <div className="fc-spinner" />
              <p>Loading…</p>
            </div>
          ) : receiptsData.filter(r => r?.paymentAmount > 0).length === 0 ? (
            <div className="db-receipts-empty">
              <div className="db-active-empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="5" width="18" height="16" rx="2" stroke="#E5E7EB" strokeWidth="1.5"/>
                  <path d="M3 10h18" stroke="#D0D5DD" strokeWidth="1.5"/>
                  <path d="M7 15h4M7 18h3" stroke="#D0D5DD" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <p className="db-active-empty-title">No Receipts Yet</p>
              <p className="db-active-empty-sub">Receipts will appear here once a payment has been processed for this loan.</p>
            </div>
          ) : (
            <div className="db-receipts-list">
              <div className="db-receipt-row db-receipt-row--header">
                <span className="db-receipt-cell">Amount</span>
                <span className="db-receipt-cell">Paid Date</span>
                <span className="db-receipt-cell db-receipt-cell--right">Receipt</span>
              </div>
              {receiptsData.filter(r => r?.paymentAmount > 0).map((item, i) => (
                <div key={i} className="db-receipt-row">
                  <div className="db-receipt-cell">
                    <p className="db-receipt-val">₹{item?.paymentAmount?.toLocaleString('en-IN')}</p>
                  </div>
                  <div className="db-receipt-cell">
                    <p className="db-receipt-val">
                      {item?.paidAt ? new Date(item.paidAt).toLocaleDateString('en-GB') : '—'}
                    </p>
                  </div>
                  <div className="db-receipt-cell db-receipt-cell--right">
                    <App1 receipt={item} type="repayment" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* ══ DESKTOP SIDEBAR ══ */}
      <aside className="db-sidebar">
        <div className="db-brand">
          <img src={Logo} alt="Feemonk" className="db-brand-logo" />
          {/* <p className="db-brand-sub">USER PORTAL</p> */}
        </div>

        <nav className="db-sidenav">
          <button
            className={`db-sidenav-item ${sideNav === 'dashboard' ? 'db-sidenav-item--active' : ''}`}
            onClick={() => setSearchParams({ tab: 'dashboard' })}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/>
              <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/>
              <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/>
              <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/>
            </svg>
            Dashboard
          </button>
           <button
            className={`db-sidenav-item ${sideNav === 'applications' ? 'db-sidenav-item--active' : ''}`}
            onClick={() => setSearchParams({ tab: 'applications' })}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
              <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="8" y1="13" x2="16" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              <line x1="8" y1="17" x2="12" y2="17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            Applications
          </button>
          <button
            className={`db-sidenav-item ${sideNav === 'active' ? 'db-sidenav-item--active' : ''}`}
            onClick={() => {
              setSearchParams({ tab: 'active' });
              if (loans.length > 0 && expandedLoan === null) {
                const firstId = loans[0].loanId;
                setExpandedLoan(firstId);
                fetchEmis(firstId);
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
              <path d="M6 10V17C6 17 8.5 20 12 20C15.5 20 18 17 18 17V10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            Active Loans
          </button>
          <button
            className={`db-sidenav-item ${sideNav === 'closed' ? 'db-sidenav-item--active' : ''}`}
            onClick={() => setSearchParams({ tab: 'closed' })}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8"/>
              <polyline points="12 6 12 12 16 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Closed Loans
          </button>
        </nav>

      </aside>

      {/* ══ DESKTOP MAIN ══ */}
      <main className="db-main">
        <div className="db-desktop-topbar">
          <div className="db-desktop-topbar-right">
            <div className="db-desktop-avatar">
              {decode?.name ? decode.name[0].toUpperCase() : 'U'}
            </div>
            <button className="db-desktop-logout" onClick={handleLogout}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round"/>
                <polyline points="16 17 21 12 16 7" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="21" y1="12" x2="9" y2="12" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
              Logout
            </button>
          </div>
        </div>

        {sideNav === 'dashboard' && <DashboardSection />}
        {sideNav === 'applications' && <ApplicationsSection />}
        {sideNav === 'active' && <ActiveSection wide />}
        {sideNav === 'closed' && <ClosedSection />}
      </main>

      {/* ══ MOBILE HEADER ══ */}
      <header className="db-mob-header">
        <div className="db-mob-brand">
          <img src={Logo} alt="Feemonk" style={{ height: 28 }} />
        </div>
        <div className="db-mob-header-right">
          <div className="db-mob-avatar">
            {decode?.name ? decode.name[0].toUpperCase() : 'U'}
          </div>
          <button className="db-mob-logout" onClick={handleLogout}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round"/>
              <polyline points="16 17 21 12 16 7" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="21" y1="12" x2="9" y2="12" stroke="#D32028" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </header>

      {/* ══ MOBILE CONTENT ══ */}
      <div className="db-mob-content">
        {sideNav === 'dashboard' && <DashboardSection />}
        {sideNav === 'active' && (
          <>
            <div className="db-mob-page-title">
              <h2>Active Loans</h2>
              <p>Your ongoing commitments</p>
            </div>
            <ActiveSection wide={true}/>
          </>
        )}
        {sideNav === 'applications' && (
          <>
            <div className="db-mob-page-title">
              <h2>Applications</h2>
              <p>Track your loan applications</p>
            </div>
            <ApplicationsSection />
          </>
        )}
        {sideNav === 'closed' && (
          <>
            <div className="db-mob-page-title">
              <h2>Closed Loans</h2>
              <p>Your loan history</p>
            </div>
            <ClosedSection />
          </>
        )}
      </div>

      {/* ══ MOBILE BOTTOM NAV ══ */}
      <nav className="db-mob-nav">
        {[
          {
            key: 'dashboard', label: 'Overview',
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/><rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/><rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/><rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/></svg>,
          },
          {
            key: 'applications', label: 'Applications',
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><line x1="8" y1="13" x2="16" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>,
          },
          {
            key: 'active', label: 'Active',
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M6 10V17C6 17 8.5 20 12 20C15.5 20 18 17 18 17V10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>,
          },
          {
            key: 'closed', label: 'Closed',
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8"/><polyline points="12 6 12 12 16 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
          },
        ].map(({ key, label, icon }) => (
          <button
            key={key}
            className={`db-mob-nav-btn ${sideNav === key ? 'db-mob-nav-btn--active' : ''}`}
            onClick={() => setSearchParams({ tab: key })}
          >
            {icon}
            <span>{label}</span>
          </button>
        ))}
      </nav>

      {/* Document viewer modal — desktop only */}
      {docViewer.open && (
        <div className="db-docviewer-overlay" onClick={() => setDocViewer({ open: false, url: '', title: '' })}>
          <div className="db-docviewer-modal" onClick={e => e.stopPropagation()}>
            <div className="db-docviewer-topbar">
              <span className="db-docviewer-title">{docViewer.title.replace('.pdf', '').replace(/([A-Z])/g, ' $1').trim()}</span>
              <div className="db-docviewer-actions">
                {/* <a href={docViewer.url} target="_blank" rel="noreferrer" className="db-docviewer-newtab">
                  Open in new tab ↗
                </a> */}
                <button className="db-docviewer-close" onClick={() => setDocViewer({ open: false, url: '', title: '' })}>✕</button>
              </div>
            </div>
            <iframe
              src={docViewer.url}
              title={docViewer.title}
              className="db-docviewer-iframe"
            />
          </div>
        </div>
      )}
    </div>
  );
}
